import { useEffect, useRef, useState, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import {
  Maximize2, Minimize2, ArrowLeft, Tv, Zap, TrendingUp, TrendingDown,
  AlertTriangle, Target, Shield, Lock, Scale, Sparkles, Crown, Skull, Flame, Bird,
  Clock, Bell, Radio,
} from "lucide-react";
import api from "@/lib/api";

// Fires up a global "empty slot" event → parent scheduler advances rotation immediately.
// Ensures viewers never stare at ghost loaders or "no data" panels.
const useSkipIfEmpty = (isReady, isEmpty) => {
  useEffect(() => {
    if (isReady && isEmpty) {
      const t = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("falcon-slot-empty"));
      }, 400); // small buffer so React finishes render before advancing
      return () => clearTimeout(t);
    }
  }, [isReady, isEmpty]);
};


/* ============================================================================
   TradingView widget helpers — free real-time streaming, no API key
   ============================================================================ */
const TVChart = memo(function TVChart({ symbol, showStudies = true }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = "";
    const inner = document.createElement("div");
    inner.className = "tradingview-widget-container__widget";
    inner.style.height = "100%";
    inner.style.width = "100%";
    ref.current.appendChild(inner);
    const s = document.createElement("script");
    s.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    s.async = true;
    // 🩹 Resolve symbol → correct TradingView exchange prefix.
    // Crypto tickers must map to a crypto exchange (BINANCE / COINBASE),
    // not NASDAQ. Otherwise TradingView shows "This symbol doesn't exist".
    const resolveTVSymbol = (raw) => {
      if (raw.includes(":")) return raw;
      const s = raw.toUpperCase();
      const CRYPTO = {
        BTC: "BINANCE:BTCUSDT",
        ETH: "BINANCE:ETHUSDT",
        SOL: "BINANCE:SOLUSDT",
        BNB: "BINANCE:BNBUSDT",
        XRP: "BINANCE:XRPUSDT",
        DOGE: "BINANCE:DOGEUSDT",
        ADA: "BINANCE:ADAUSDT",
        AVAX: "BINANCE:AVAXUSDT",
      };
      if (CRYPTO[s]) return CRYPTO[s];
      // Common index proxies
      const INDEX = {
        SPX: "SP:SPX", SPY: "AMEX:SPY", QQQ: "NASDAQ:QQQ",
        DIA: "AMEX:DIA", IWM: "AMEX:IWM", VIX: "TVC:VIX",
      };
      if (INDEX[s]) return INDEX[s];
      return `NASDAQ:${s}`;
    };
    s.innerHTML = JSON.stringify({
      autosize: true,
      symbol: resolveTVSymbol(symbol),
      interval: "5",
      timezone: "America/New_York",
      theme: "dark",
      style: "1",
      locale: "en",
      hide_side_toolbar: true,
      allow_symbol_change: false,
      studies: showStudies ? [
        "MASimple@tv-basicstudies",
        "MAExp@tv-basicstudies",
        "RSI@tv-basicstudies",
        "BB@tv-basicstudies",
      ] : [],
      backgroundColor: "rgba(6, 10, 18, 1)",
      gridColor: "rgba(60, 70, 90, 0.25)",
    });
    ref.current.appendChild(s);
  }, [symbol, showStudies]);
  return <div ref={ref} className="tradingview-widget-container h-full w-full" />;
});

/* Calm, continuous CNBC-style bottom ticker tape (native TradingView widget) */
const TVTickerTape = memo(function TVTickerTape({ items }) {
  const ref = useRef(null);
  // Serialize items once — new script only when the list actually changes
  const key = (items || []).map((i) => i.symbol).join("|");
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = "";
    const inner = document.createElement("div");
    inner.className = "tradingview-widget-container__widget";
    ref.current.appendChild(inner);
    const symbols = (items || []).map((i) => ({ proName: i.symbol, title: i.name }));
    const s = document.createElement("script");
    s.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    s.async = true;
    s.innerHTML = JSON.stringify({
      symbols,
      showSymbolLogo: true,
      isTransparent: true,
      displayMode: "adaptive",
      colorTheme: "dark",
      locale: "en",
    });
    ref.current.appendChild(s);
  }, [key]);
  return <div ref={ref} className="tradingview-widget-container w-full" />;
});

/* ============================================================================
   Live Clock
   ============================================================================ */
const LiveClock = ({ session }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const ct = now.toLocaleString("en-US", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  const date = now.toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric" });
  const sessionLabel = {
    open: { text: "Live · Open", color: "bg-emerald-500/20 text-emerald-300", dot: "bg-emerald-400 animate-pulse" },
    pre:  { text: "Pre-Market",  color: "bg-sky-500/20 text-sky-300",         dot: "bg-sky-400 animate-pulse" },
    after:{ text: "After-Hours", color: "bg-amber-500/20 text-amber-300",     dot: "bg-amber-400 animate-pulse" },
    closed:{text: "Closed",     color: "bg-slate-800/60 text-slate-400",     dot: "bg-slate-500" },
  }[session || "closed"];
  return (
    <div className="flex items-center gap-4" data-testid="tv-clock">
      <div className="text-right">
        <div className="font-mono text-3xl font-black tabular-nums text-white" data-testid="tv-clock-time">{ct}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400" data-testid="tv-clock-date">{date} · Chicago</div>
      </div>
      <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 ${sessionLabel.color}`} data-testid="tv-session-badge">
        <span className={`h-2 w-2 rounded-full ${sessionLabel.dot}`} />
        <span className="text-xs font-black uppercase tracking-wider">{sessionLabel.text}</span>
      </div>
    </div>
  );
};

/* ============================================================================
   BigScrollTicker — FIERCE big-font custom scroll (used for MARKET indices row
   at the bottom). Accepts items with { symbol, name?, price, change_pct, change_abs?, flash? }

   Wrapped in React.memo with a symbol-based equality check: re-render only
   when the symbol list actually changes. Prevents the CSS marquee animation
   from resetting mid-scroll whenever the parent re-renders (which was causing
   the ticker to visibly snap back to the first stock every few seconds).
   ============================================================================ */
const BigScrollTickerBase = ({ items, testidPrefix = "tv-big" }) => {
  // 🛡️ Only render items with a real price — prevents null-price entries
  // from making the two halves of the doubled track different widths, which
  // was causing the visible jump-back-to-DOW-JONES bug at loop wraparound.
  const clean = (items || []).filter((r) => r && r.symbol && r.price != null);
  if (clean.length === 0) return (
    <div className="px-3 py-2 text-center text-xs uppercase tracking-widest text-slate-600">
      Loading market feed…
    </div>
  );
  const doubled = [...clean, ...clean];
  return (
    <div className="tv-ticker-wrap relative overflow-hidden">
      <div className="tv-ticker-track flex items-center whitespace-nowrap py-3">
        {doubled.map((row, i) => {
          const cp = row.change_pct;
          const px = row.price;
          const abs = row.change_abs;
          const isUp = cp !== null && cp !== undefined && cp >= 0;
          const isFlash = row.flash;
          const pctStr = cp !== null && cp !== undefined ? Math.abs(cp).toFixed(2) : "—";
          const pxFmt = px != null ? (px >= 1000 ? px.toFixed(0) : px.toFixed(2)) : null;
          const absFmt = abs != null ? `${abs >= 0 ? "+" : ""}${abs.toFixed(2)}` : null;
          return (
            <div
              key={`${row.symbol}-${i}`}
              className={`flex shrink-0 items-center gap-4 rounded-lg px-4 py-1 mr-10 ${
                isFlash ? (isUp ? "bg-emerald-500/20 ring-2 ring-emerald-400 animate-pulse-fast" : "bg-red-500/20 ring-2 ring-red-400 animate-pulse-fast") : ""
              }`}
              data-testid={`${testidPrefix}-item-${row.symbol}`}
            >
              <span className="font-mono text-3xl font-black text-white">{row.name || row.symbol}</span>
              {pxFmt && (
                <span className="font-mono text-4xl font-black text-slate-100">${pxFmt}</span>
              )}
              {absFmt && (
                <span className={`font-mono text-2xl font-bold ${isUp ? "text-emerald-300" : "text-red-300"}`}>
                  {absFmt}
                </span>
              )}
              {cp !== null && cp !== undefined ? (
                <span className={`font-mono text-3xl font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                  {isUp ? "▲" : "▼"} {pctStr}%
                </span>
              ) : (
                <span className="font-mono text-2xl text-slate-500">—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================================================
   CNBCTicker — TOP row of the bottom rail. Shows 40+ mega-cap/sector-leader
   stocks with symbol · price · Δ$ · Δ% in a fast horizontal scroll (right→left).

   Memoised on the joined symbol list — re-renders only when the tickers change,
   NOT on every price tick (which would reset the CSS scroll and cause the
   visible-jump-back-to-AMZN bug the user reported).
   ============================================================================ */
const CNBCTickerBase = ({ items }) => {
  // 🛡️ Only render items that have a real price. Prevents partial payloads
  // from producing narrow half-width entries that break the seamless loop.
  const clean = (items || []).filter((r) => r && r.symbol && r.price != null);
  if (clean.length === 0) return (
    <div className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-slate-700">
      Loading market wire…
    </div>
  );
  // Force a duplicated track for the seamless -50% loop.
  const doubled = [...clean, ...clean];
  return (
    <div className="tv-ticker-wrap relative overflow-hidden bg-black">
      <div className="tv-cnbc-track flex items-center whitespace-nowrap py-1.5">
        {doubled.map((row, i) => {
          const cp = row.change_pct;
          const abs = row.change_abs;
          const isUp = cp !== null && cp !== undefined && cp >= 0;
          const pxFmt = row.price != null ? (row.price >= 1000 ? row.price.toFixed(0) : row.price.toFixed(2)) : null;
          const absFmt = abs != null ? `${abs >= 0 ? "+" : ""}${abs.toFixed(2)}` : null;
          const pctStr = cp !== null && cp !== undefined ? `${cp >= 0 ? "+" : ""}${cp.toFixed(2)}%` : "";
          return (
            <div
              key={`${row.symbol}-${i}`}
              className="flex shrink-0 items-center gap-2 px-1 mr-6"
              data-testid={`tv-cnbc-item-${row.symbol}`}
            >
              <span className="font-mono text-sm font-black text-white/90">{row.symbol}</span>
              {pxFmt && <span className="font-mono text-sm font-bold text-slate-300">{pxFmt}</span>}
              {absFmt && <span className={`font-mono text-xs font-bold ${isUp ? "text-emerald-400" : "text-red-400"}`}>{absFmt}</span>}
              {pctStr && <span className={`font-mono text-xs font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>{pctStr}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Memo wrappers — the CSS scroll animation only survives if these components
// don't re-render on every parent state tick. Custom equality check ignores
// price fluctuations and only compares the symbol list.
const tickersEqualBySymbolList = (prev, next) => {
  if (prev.items === next.items) return true;
  const a = prev.items || [];
  const b = next.items || [];
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.symbol !== b[i]?.symbol) return false;
  }
  return true;
};
const BigScrollTicker = memo(BigScrollTickerBase, tickersEqualBySymbolList);
const CNBCTicker      = memo(CNBCTickerBase,      tickersEqualBySymbolList);

/* ============================================================================
   Portfolio Pulse Slot — big tile grid
   ============================================================================ */
/* ============================================================================
   TrendSpider Feed Slot — cycles through their latest chart posts (via Nitter)
   ============================================================================ */
const TrendSpiderSlot = () => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/trendspider-feed?limit=8")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const items = data?.items || [];
  useEffect(() => {
    if (items.length === 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Loading TrendSpider charts…</div>;
  if (!data.ok || items.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500" data-testid="tv-trendspider-slot">
      <div>
        <div className="mb-2 text-4xl">📡</div>
        <div className="text-sm">TrendSpider feed unavailable right now</div>
        <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-600">(Nitter mirrors offline — retrying in 15 min)</div>
      </div>
    </div>
  );

  const cur = items[idx % items.length];
  return (
    <div className="grid h-full grid-cols-[1fr_360px] gap-3" data-testid="tv-trendspider-slot">
      <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/60">
        {cur.image_url ? (
          <img
            src={cur.image_url}
            alt={cur.text}
            className="h-full w-full object-contain"
            data-testid="tv-trendspider-image"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-500">No chart image on this post</div>
        )}
      </div>
      <aside className="flex flex-col gap-3 rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 via-slate-950 to-slate-950 p-4">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-cyan-300" />
          <div className="text-[10px] font-black uppercase tracking-[0.32em] text-cyan-300">TrendSpider · Live Feed</div>
        </div>
        <div className="text-sm leading-relaxed text-slate-100">{cur.text}</div>
        {cur.tickers?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {cur.tickers.map((t) => (
              <span key={t} className="rounded-full bg-cyan-500/20 px-2 py-1 font-mono text-sm font-black text-cyan-200" data-testid={`tv-ts-ticker-${t}`}>${t}</span>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between text-[9px] uppercase tracking-widest text-slate-600">
          <span>Post {(idx % items.length) + 1} / {items.length}</span>
          <span>via {data.mirror_used?.replace("https://", "")}</span>
        </div>
      </aside>
    </div>
  );
};

/* ============================================================================
   Ripe Trades Slot — our own version. Sohel analysis picks top setups
   with a simple TradingView chart + 1-line AI advice.
   ============================================================================ */
const RipeTradesSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/ripe-trades")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const ripe = data?.ripe || [];
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Scanning your book for ripe setups…</div>;
  if (ripe.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500" data-testid="tv-ripe-trades-slot">
      <div>
        <Target className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">No ripe setups right now.</div>
        <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-600">Scanning {data.candidate_count} names · need confidence ≥ 70% + clean entry/stop/target.</div>
      </div>
    </div>
  );

  const gridCols = ripe.length >= 3 ? "grid-cols-2" : "grid-cols-1";
  return (
    <div className={`grid h-full ${gridCols} gap-3`} data-testid="tv-ripe-trades-slot">
      {ripe.map((t) => {
        const isLong = t.bias === "LONG";
        return (
          <div
            key={t.symbol}
            className={`grid grid-cols-[1fr_180px] overflow-hidden rounded-2xl border ${
              isLong ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950"
                     : "border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950"
            }`}
            data-testid={`tv-ripe-${t.symbol}`}
          >
            {/* TradingView mini chart */}
            <div className="h-full min-h-[140px] bg-black">
              <TVChart symbol={t.tradingview_symbol} />
            </div>
            <div className="flex flex-col justify-between gap-1 p-3">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-xl font-black text-white">{t.symbol}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${isLong ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                    {t.bias} · {t.confidence}%
                  </span>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">
                  Entry ${t.entry?.toFixed(2)} · Stop ${t.stop?.toFixed(2)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                  Target ${t.target?.toFixed(2)}{t.risk_reward ? ` · ${t.risk_reward.toFixed(1)}R` : ""}
                </div>
              </div>
              <div className={`text-[10px] leading-snug ${isLong ? "text-emerald-100" : "text-red-100"}`} data-testid={`tv-ripe-why-${t.symbol}`}>
                {t.why_now}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================================
   PortfolioPulseSlot — split into TWO slides (page 1 & 2), sorted by weight%
   high→low. Each tile: SYMBOL · Company Name · BIG price · Δ%. Roomier layout.
   ============================================================================ */
const PortfolioPulseSlot = ({ pulse, page = 1 }) => {
  const PER_PAGE = 12;
  // Sort by weight_pct desc; unweighted rows sink to the end
  const sorted = [...(pulse || [])].sort((a, b) => (b.weight_pct || 0) - (a.weight_pct || 0));
  const start = (page - 1) * PER_PAGE;
  const rows = sorted.slice(start, start + PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));

  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-center">
        <div>
          <div className="mb-3 text-6xl">📭</div>
          <div className="text-lg font-bold text-slate-300">No portfolio holdings yet</div>
          <div className="mt-1 text-sm text-slate-500">Add positions in your Portfolio to populate Falcon View</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid={`tv-pulse-page-${page}`}>
      <div className="mb-3 flex items-center gap-3">
        <TrendingUp className="h-5 w-5 text-amber-400" />
        <h2 className="text-sm font-black uppercase tracking-[0.24em] text-amber-300">
          Your Portfolio · Page {page} / {totalPages}
        </h2>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500">
          {sorted.length} holdings · sorted by weight
        </span>
      </div>
      <div className="grid flex-1 grid-cols-3 grid-rows-4 gap-3 overflow-hidden">
        {rows.map((row) => {
          const cp = row.change_pct;
          const isUp = cp !== null && cp !== undefined && cp >= 0;
          return (
            <div
              key={row.symbol}
              className={`relative flex flex-col justify-between overflow-hidden rounded-xl border p-4 ${
                isUp ? "border-emerald-500/25 bg-gradient-to-br from-emerald-500/8 via-slate-950 to-slate-950"
                     : cp !== null && cp < 0 ? "border-red-500/25 bg-gradient-to-br from-red-500/8 via-slate-950 to-slate-950"
                     : "border-slate-800 bg-slate-950/60"
              }`}
              data-testid={`tv-pulse-tile-${row.symbol}`}
            >
              <div>
                <div className="flex items-baseline gap-2">
                  <div className="font-mono text-3xl font-black text-white">{row.symbol}</div>
                  {row.weight_pct != null && (
                    <span className="ml-auto rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-300">
                      {row.weight_pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                {row.company_name && row.company_name !== row.symbol && (
                  <div className="mt-0.5 truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {row.company_name}
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                {row.price != null ? (
                  <div className="font-mono text-4xl font-black text-slate-100">
                    ${row.price >= 1000 ? row.price.toFixed(0) : row.price.toFixed(2)}
                  </div>
                ) : (
                  <div className="font-mono text-2xl text-slate-600">—</div>
                )}
                {cp !== null && cp !== undefined ? (
                  <div className={`font-mono text-2xl font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                    {isUp ? "▲" : "▼"} {Math.abs(cp).toFixed(2)}%
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================================================
   IRA Slot — combined long-term IRA pool (Roth IRA + Roth Contributory IRA
   aggregated). Shows the whole basket + today's weighted % + all-time return.
   ============================================================================ */
const IRASlot = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const failsRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/ira")
      .then((r) => { if (!cancelled) { failsRef.current = 0; setData(r.data); setStatus("ok"); } })
      .catch(() => { if (!cancelled && (failsRef.current += 1) >= 3) setStatus("error"); });
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const holdings = data?.holdings || [];
  const hasData = data && Array.isArray(data.holdings);

  // Distinguish 'still loading' from 'confirmed empty' from 'error' so the
  // panel never spins forever. A successful response with zero holdings is a
  // real empty state — add holdings in Manage Deployments → Database.
  if (!hasData) {
    if (status === "error") return (
      <div className="flex h-full items-center justify-center px-6 text-center text-red-400/80" data-testid="tv-ira-error">
        IRA pool unavailable — retrying…
      </div>
    );
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Loading IRA pool…</div>
    );
  }
  if (holdings.length === 0) return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-400" data-testid="tv-ira-empty">
      <Shield className="h-8 w-8 text-sky-300/60" />
      <div className="text-sm font-semibold text-slate-300">No IRA holdings yet</div>
      <div className="text-xs text-slate-500">Add your holdings in Manage Deployments → Database (collection <span className="font-mono">ira_holdings</span>).</div>
    </div>
  );

  const dayPct = data.day_change_pct ?? 0;
  const dayUp = dayPct >= 0;
  const allTimePct = data.all_time_return_pct ?? 0;
  const allTimeUp = allTimePct >= 0;

  return (
    <div className="flex h-full flex-col gap-3" data-testid="tv-ira-slot">
      {/* Header — big summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-3 flex items-center gap-3 rounded-xl border border-sky-500/40 bg-gradient-to-r from-sky-500/10 via-slate-950 to-slate-950 p-3">
          <Shield className="h-8 w-8 text-sky-300" />
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.32em] text-sky-300">IRA · Long-Term Pool</div>
            <div className="text-xl font-black text-white">Retirement bucket · {holdings.length} names</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Today</div>
            <div className={`font-mono text-4xl font-black ${dayUp ? "text-emerald-400" : "text-red-400"}`} data-testid="tv-ira-day-change">
              {dayUp ? "▲" : "▼"} {Math.abs(dayPct).toFixed(2)}%
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">All-Time</div>
            <div className={`font-mono text-2xl font-black ${allTimeUp ? "text-emerald-400" : "text-red-400"}`} data-testid="tv-ira-alltime">
              {allTimeUp ? "+" : ""}{allTimePct.toFixed(2)}%
            </div>
          </div>
        </div>
      </div>
      {/* Holdings grid — sorted by weight% (value desc) */}
      <div className="grid flex-1 grid-cols-6 gap-2 overflow-hidden">
        {holdings.slice(0, 30).map((h) => {
          const cp = h.change_pct;
          const isUp = cp !== null && cp !== undefined && cp >= 0;
          return (
            <div
              key={h.symbol}
              className="flex flex-col justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-2"
              data-testid={`tv-ira-tile-${h.symbol}`}
            >
              <div>
                <div className="font-mono text-lg font-black text-white">{h.symbol}</div>
                {h.company_name && (
                  <div className="truncate text-[9px] uppercase tracking-wider text-slate-500">{h.company_name}</div>
                )}
              </div>
              {cp !== null && cp !== undefined ? (
                <div className={`mt-1 font-mono text-lg font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                  {isUp ? "▲" : "▼"} {Math.abs(cp).toFixed(2)}%
                </div>
              ) : (
                <div className="mt-1 font-mono text-sm text-slate-600">—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================================================
   Deep Dive Slot — chart + Sohel Analysis panel
   ============================================================================ */
const DeepDiveSlot = ({ symbol }) => {
  const [dd, setDd] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/falcon-view/deep-dive/${symbol}`)
      .then((r) => { if (!cancelled) setDd(r.data); })
      .catch(() => { if (!cancelled) setDd({ ok: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol]);

  const tvSym = dd?.tradingview_symbol || `NASDAQ:${symbol}`;
  const ind = dd?.indicators || {};

  return (
    <div className="grid h-full grid-cols-[1fr_420px] gap-3">
      {/* Chart */}
      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/60">
        <div className="flex items-center justify-between border-b border-slate-800/60 bg-black/40 px-3 py-2">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xl font-black text-white">{symbol}</span>
            {dd?.bias && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                dd.bias === "LONG" ? "bg-emerald-500/20 text-emerald-300" :
                dd.bias === "SHORT" ? "bg-red-500/20 text-red-300" :
                "bg-slate-700 text-slate-300"
              }`}>
                {dd.bias} · {dd.confidence ?? "—"}%
              </span>
            )}
            {dd?.pattern && (
              <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-300">
                {dd.pattern}
              </span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Live · TradingView</span>
        </div>
        <div className="h-[calc(100%-40px)]">
          <TVChart symbol={tvSym} />
        </div>
      </div>

      {/* Sohel Analysis panel — MUCH more detailed */}
      <aside className="flex flex-col gap-2.5 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/60 p-3" data-testid="tv-sohel-analysis">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-400" />
          <h3 className="text-xs font-black uppercase tracking-[0.24em] text-amber-300">Sohel Analysis · {symbol}</h3>
        </div>
        {loading ? (
          <div className="mt-4 text-center text-xs text-slate-500">Analyzing…</div>
        ) : !dd?.ok ? (
          <div className="mt-4 rounded-lg bg-slate-900/60 p-3 text-center text-xs text-slate-400">
            {dd?.error || `Analysis unavailable for ${symbol}`}
          </div>
        ) : (
          <>
            {/* Narrative — the "why" prose */}
            {(dd.narrative || []).length > 0 && (
              <div className="rounded-lg bg-slate-900/40 p-2.5">
                <div className="mb-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">The Read</div>
                <div className="space-y-1.5">
                  {(dd.narrative || []).map((p, i) => (
                    <p key={i} className="text-[12px] leading-snug text-slate-200">{p}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Indicators */}
            <div className="grid grid-cols-3 gap-1.5">
              <div className="rounded-md bg-slate-900/40 p-2">
                <div className="text-[9px] uppercase text-slate-500">RSI</div>
                <div className={`font-mono text-xl font-black ${
                  (ind.rsi || 50) >= 70 ? "text-red-400" :
                  (ind.rsi || 50) <= 30 ? "text-emerald-400" :
                  "text-white"
                }`}>{ind.rsi != null ? ind.rsi.toFixed(0) : "—"}</div>
              </div>
              <div className="rounded-md bg-slate-900/40 p-2">
                <div className="text-[9px] uppercase text-slate-500">ADX</div>
                <div className={`font-mono text-xl font-black ${
                  (ind.adx || 0) >= 25 ? "text-amber-300" :
                  (ind.adx || 0) < 20 ? "text-slate-500" :
                  "text-white"
                }`}>{ind.adx != null ? ind.adx.toFixed(0) : "—"}</div>
              </div>
              <div className="rounded-md bg-slate-900/40 p-2">
                <div className="text-[9px] uppercase text-slate-500">Stoch</div>
                <div className={`font-mono text-xl font-black ${
                  (ind.stoch_rsi || 50) >= 80 ? "text-red-400" :
                  (ind.stoch_rsi || 50) <= 20 ? "text-emerald-400" :
                  "text-white"
                }`}>{ind.stoch_rsi != null ? ind.stoch_rsi.toFixed(0) : "—"}</div>
              </div>
            </div>

            {/* MTF */}
            {dd.mtf_alignment?.alignment && (
              <div className="flex items-center justify-between rounded-md bg-slate-900/40 px-2 py-1.5 text-xs">
                <span className="text-slate-400">Multi-Timeframe (1H · 4H · 1D)</span>
                <span className={`font-black uppercase tracking-wider ${
                  dd.mtf_alignment.alignment === "BULLISH_ALIGNED" ? "text-emerald-400" :
                  dd.mtf_alignment.alignment === "BEARISH_ALIGNED" ? "text-red-400" :
                  "text-amber-400"
                }`}>
                  {dd.mtf_alignment.alignment.replace(/_/g, " ")}
                </span>
              </div>
            )}

            {/* Warnings — show ALL */}
            {(dd.warnings || []).length > 0 && (
              <div>
                <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Signals & Warnings</div>
                <ul className="space-y-1">
                  {(dd.warnings || []).map((w, i) => (
                    <li key={i} className="text-[11px] leading-snug text-slate-200">{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trade plan — step by step */}
            {dd.trade_plan?.ok && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5">
                <div className="mb-1 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-300">
                  <Target className="h-3 w-3" /> Trade Plan
                </div>
                <ul className="space-y-1">
                  {(dd.trade_plan.steps || []).map((s, i) => (
                    <li key={i} className="text-[11px] leading-snug text-emerald-100">{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {dd.trade_plan?.ok === false && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-2 text-[11px] text-slate-400">
                {dd.trade_plan.reason}
              </div>
            )}

            {/* Action shorthand */}
            {dd.action && (
              <div className="mt-auto rounded-lg border border-amber-500/30 bg-amber-500/10 p-2">
                <div className="mb-1 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-300">
                  <Target className="h-3 w-3" /> Action
                </div>
                <div className="text-xs leading-snug text-amber-100">{dd.action}</div>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

/* ============================================================================
   Whale Alert Slot — with classification (Directional / Hedge / Short-Cover / Unusual)
   ============================================================================ */
const CLASS_STYLE = {
  DIRECTIONAL: { color: "emerald", Icon: Target,   sub: "Follow the money" },
  HEDGE:       { color: "sky",     Icon: Shield,   sub: "Protecting a position — don't follow" },
  SHORT_COVER: { color: "amber",   Icon: Lock,     sub: "Position closing/rolling — not opening" },
  NEUTRAL:     { color: "slate",   Icon: Scale,    sub: "Unclear — not enough signal" },
};

/* One whale row — compact horizontal layout so 2 fit per screen */
const WhaleCard = ({ whale }) => {
  const style = CLASS_STYLE[whale.classification] || CLASS_STYLE.NEUTRAL;
  const isCall = (whale.contract_type || "").toUpperCase().includes("CALL");
  return (
    <div
      className={`grid h-full grid-cols-[1.4fr_260px] overflow-hidden rounded-xl border border-${style.color}-500/40 bg-gradient-to-br from-${style.color}-500/10 via-slate-950 to-slate-950`}
      data-testid={`tv-whale-card-${whale.symbol}`}
    >
      {/* LEFT — main info */}
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-${style.color}-500/20`}>
              <style.Icon className={`h-5 w-5 text-${style.color}-300`} />
            </div>
            <div>
              <div className={`text-[10px] font-black uppercase tracking-[0.24em] text-${style.color}-300`}>Whale Alert</div>
              <div className="text-xl font-black text-white">{whale.label}</div>
            </div>
          </div>
          {whale.unusual && (
            <div className="flex animate-pulse items-center gap-1.5 rounded-full bg-red-500/20 px-2.5 py-1 ring-2 ring-red-400">
              <AlertTriangle className="h-3.5 w-3.5 text-red-300" />
              <span className="text-[10px] font-black uppercase tracking-widest text-red-200">Unusual</span>
            </div>
          )}
        </div>

        <div className="flex items-baseline gap-4">
          <div className="font-mono text-5xl font-black text-white">{whale.symbol}</div>
          {whale.company_name && whale.company_name !== whale.symbol && (
            <div className="text-sm font-bold uppercase tracking-wider text-slate-300 truncate" data-testid={`tv-whale-name-${whale.symbol}`}>
              {whale.company_name}
            </div>
          )}
          <div className={`rounded-lg px-2.5 py-1 font-mono text-xl font-black ${isCall ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
            {isCall ? "CALL" : "PUT"} ${whale.strike}
          </div>
          <div className="text-sm text-slate-400">exp {whale.expiry}</div>
          {whale.is_sweep && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-orange-300 ring-1 ring-orange-500/50">
              <Zap className="h-3 w-3" /> Sweep
            </span>
          )}
        </div>

        <div className="grid flex-1 grid-cols-4 gap-2">
          <div className="flex flex-col justify-center rounded-lg bg-slate-900/60 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Premium</div>
            <div className="font-mono text-xl font-black text-amber-300">{whale.premium_display || `$${(whale.premium / 1000).toFixed(0)}K`}</div>
          </div>
          <div className="flex flex-col justify-center rounded-lg bg-slate-900/60 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Volume</div>
            <div className="font-mono text-xl font-black text-white">{whale.volume?.toLocaleString() || "—"}</div>
          </div>
          <div className="flex flex-col justify-center rounded-lg bg-slate-900/60 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Confidence</div>
            <div className={`font-mono text-xl font-black text-${style.color}-300`}>{whale.confidence_pct}%</div>
            <div className={`text-[8px] font-black uppercase text-${style.color}-400`}>{whale.confidence_tier}</div>
          </div>
          <div className="flex flex-col justify-center rounded-lg bg-slate-900/60 p-2.5">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Stock Move</div>
            <div className={`font-mono text-xl font-black ${
              (whale.underlying_change_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"
            }`}>
              {whale.underlying_change_pct != null ? `${whale.underlying_change_pct >= 0 ? "+" : ""}${whale.underlying_change_pct.toFixed(2)}%` : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — reasoning */}
      <aside className="flex flex-col gap-2 overflow-y-auto border-l border-slate-800/70 bg-slate-950/40 p-3">
        <div>
          <div className="mb-0.5 text-[9px] font-black uppercase tracking-[0.24em] text-slate-500">Recommendation</div>
          <div className={`text-xs font-bold leading-snug text-${style.color}-300`}>{whale.recommendation}</div>
        </div>
        {whale.unusual_reason && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-1.5">
            <div className="text-[9px] font-black uppercase text-red-300">Why unusual</div>
            <div className="text-[10px] leading-snug text-red-100">{whale.unusual_reason}</div>
          </div>
        )}
        <div>
          <div className="mb-0.5 text-[9px] font-black uppercase tracking-[0.24em] text-slate-500">How I classified this</div>
          <ul className="space-y-0.5">
            {(whale.signals || []).slice(0, 4).map((s, i) => (
              <li key={i} className="flex gap-1 text-[10px] leading-snug text-slate-300">
                <span className="text-slate-600">▸</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
};

const WhaleSlot = ({ whales }) => {
  const list = (whales || []).filter(Boolean).slice(0, 2);
  if (list.length === 0) return (
    <div className="flex h-full items-center justify-center text-center">
      <div>
        <div className="mb-3 text-6xl">🐋</div>
        <div className="text-lg font-bold text-slate-300">No big whale trades right now</div>
        <div className="mt-1 text-sm text-slate-500">Watching for ≥ $500K premium contracts…</div>
      </div>
    </div>
  );
  return (
    <div className="grid h-full grid-rows-2 gap-3">
      {list.map((w, i) => <WhaleCard key={`${w.symbol}-${i}`} whale={w} />)}
    </div>
  );
};

/* ============================================================================
   Movers Slot — one calm slide with UP movers (left) and DOWN movers (right)
   Replaces the intrusive full-screen flash overlay.
   ============================================================================ */
const MoversSlot = ({ pulse }) => {
  // Only show meaningful movers — ≥ 4% up or down
  const gainers = (pulse || [])
    .filter((p) => (p.change_pct ?? 0) >= 4)
    .sort((a, b) => (b.change_pct || 0) - (a.change_pct || 0))
    .slice(0, 8);
  const losers = (pulse || [])
    .filter((p) => (p.change_pct ?? 0) <= -4)
    .sort((a, b) => (a.change_pct || 0) - (b.change_pct || 0))
    .slice(0, 8);

  const renderList = (rows, isUp) => (
    <div className={`flex flex-col overflow-hidden rounded-2xl border p-4 ${
      isUp ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950"
           : "border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950"
    }`}>
      <div className="mb-3 flex items-center gap-3">
        {isUp ? <TrendingUp className="h-6 w-6 text-emerald-400" /> : <TrendingDown className="h-6 w-6 text-red-400" />}
        <h2 className={`text-lg font-black uppercase tracking-[0.24em] ${isUp ? "text-emerald-300" : "text-red-300"}`}>
          {isUp ? "Going Up (≥ 4%)" : "Going Down (≥ 4%)"}
        </h2>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500">
          {rows.length} names
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
          — nothing moved 4%+ {isUp ? "up" : "down"} —
        </div>
      ) : (
        <ul className="flex-1 space-y-1.5 overflow-hidden">
          {rows.map((r) => (
            <li
              key={r.symbol}
              className={`flex items-center justify-between rounded-lg px-3 py-2 bg-slate-900/50`}
              data-testid={`tv-mover-${r.symbol}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-2xl font-black text-white">{r.symbol}</span>
                {r.weight_pct != null && (
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    {r.weight_pct.toFixed(2)}% weight
                  </span>
                )}
              </div>
              <div className={`font-mono text-2xl font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                {isUp ? "▲" : "▼"} {Math.abs(r.change_pct || 0).toFixed(2)}%
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="grid h-full grid-cols-2 gap-4" data-testid="tv-movers-slot">
      {renderList(gainers, true)}
      {renderList(losers, false)}
    </div>
  );
};

/* ============================================================================
   Plays Slot — options positions (called PLAYS, never options).
   Shows each play with % return, DTE, ITM/OTM tag, and expiry warnings.
   ============================================================================ */
const PlaysSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/plays")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const plays = data?.plays || [];
  if (plays.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500">
      <div>
        <Target className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">No active plays</div>
      </div>
    </div>
  );

  return (
    <div className="grid h-full grid-cols-3 gap-3" data-testid="tv-plays-slot">
      {plays.slice(0, 9).map((p) => {
        const rp = p.approx_return_pct;
        const isUp = (p.total_return || 0) >= 0;
        const urg = p.urgency;
        const urgColor = urg === "EXPIRED" || urg === "CRITICAL" ? "red" : urg === "SOON" ? "amber" : "slate";
        return (
          <div
            key={`${p.symbol}-${p.strike}-${p.expiry}`}
            className={`flex flex-col overflow-hidden rounded-xl border p-3 bg-slate-950/60 ${
              urg === "CRITICAL" || urg === "EXPIRED" ? "border-red-500/60 animate-pulse-slow" :
              urg === "SOON" ? "border-amber-500/50" :
              isUp ? "border-emerald-500/30" : "border-slate-800"
            }`}
            data-testid={`tv-play-${p.symbol}-${p.strike}`}
          >
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-mono text-2xl font-black text-white">{p.symbol}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Stock Play · ${p.strike} {p.contract_type}
                </div>
              </div>
              {p.is_itm && (
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-300">ITM</span>
              )}
            </div>
            {rp !== null && rp !== undefined && (
              <div className={`mt-2 font-mono text-3xl font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                Play is {isUp ? "up" : "down"} {Math.abs(rp).toFixed(2)}%
              </div>
            )}
            <div className="mt-auto flex items-center justify-between text-[10px]">
              <span className="text-slate-500">exp {p.expiry}</span>
              {p.days_to_expiry != null && (
                <span className={`font-bold uppercase tracking-widest text-${urgColor}-300`}>
                  {p.days_to_expiry <= 0 ? "EXPIRED" : `${p.days_to_expiry} days left`}
                </span>
              )}
            </div>
            {p.expiry_warning && (
              <div className={`mt-1.5 rounded-md bg-${urgColor}-500/15 px-2 py-1 text-[10px] font-bold text-${urgColor}-200`} data-testid={`tv-play-warning-${p.symbol}-${p.strike}`}>
                <Clock className="mr-1 inline h-3 w-3" /> {p.expiry_warning}
              </div>
            )}
            {p.advice && (
              <div className={`mt-1.5 rounded-md border p-1.5 ${
                p.advice.verdict === "GRAB PROFIT" ? "border-emerald-500/40 bg-emerald-500/10" :
                p.advice.verdict === "CUT LOSS"    ? "border-red-500/40 bg-red-500/10" :
                p.advice.verdict === "ROLL"        ? "border-sky-500/40 bg-sky-500/10" :
                "border-slate-700 bg-slate-800/40"
              }`} data-testid={`tv-play-advice-${p.symbol}-${p.strike}`}>
                <div className="flex items-center gap-1.5">
                  <Sparkles className={`h-3 w-3 ${
                    p.advice.verdict === "GRAB PROFIT" ? "text-emerald-300" :
                    p.advice.verdict === "CUT LOSS"    ? "text-red-300" :
                    p.advice.verdict === "ROLL"        ? "text-sky-300" :
                    "text-slate-400"
                  }`} />
                  <span className={`text-[10px] font-black uppercase tracking-[0.16em] ${
                    p.advice.verdict === "GRAB PROFIT" ? "text-emerald-300" :
                    p.advice.verdict === "CUT LOSS"    ? "text-red-300" :
                    p.advice.verdict === "ROLL"        ? "text-sky-300" :
                    "text-slate-400"
                  }`}>
                    {p.advice.verdict}
                    {p.advice.verdict === "GRAB PROFIT" && p.advice.target_pct > 0 ? ` at +${p.advice.target_pct}%` : ""}
                    {p.advice.verdict === "CUT LOSS"    && p.advice.stop_pct   < 0 ? ` at ${p.advice.stop_pct}%` : ""}
                  </span>
                </div>
                <div className="mt-0.5 text-xs leading-snug text-slate-200">{p.advice.reason}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================================
   Sudden Alerts Slot — full slide showing recent sudden portfolio movements
   with AI reasoning (technical + fundamental).
   ============================================================================ */
const SuddenAlertsSlot = ({ alerts }) => {
  const active = alerts?.active || [];
  const archived = alerts?.archived || [];
  const [analyst, setAnalyst] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/analyst-alerts")
      .then((r) => { if (!cancelled) setAnalyst(r.data?.alerts || []); })
      .catch(() => {});
    load();
    const t = setInterval(load, 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return (
    <div className="grid h-full grid-cols-[2fr_1fr] gap-3" data-testid="tv-sudden-alerts-slot">
      {/* Active (last hour) */}
      <div className="flex flex-col overflow-hidden rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Bell className={`h-5 w-5 text-red-300 ${active.length > 0 ? "animate-pulse" : ""}`} />
          <h2 className="text-sm font-black uppercase tracking-[0.24em] text-red-200">Active Alerts · Last Hour</h2>
          <span className="ml-auto rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-black text-red-200">{active.length}</span>
        </div>
        {active.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-center">
            <div>
              <Radio className="mx-auto mb-2 h-8 w-8 text-slate-600" />
              <div className="text-sm text-slate-400">All quiet — no sudden moves yet.</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-600">Watching for ≥ 2% moves in 5 minutes.</div>
            </div>
          </div>
        ) : (
          <ul className="flex-1 space-y-2 overflow-y-auto pr-1">
            {active.map((a) => {
              const isUp = a.delta_pct > 0;
              return (
                <li key={`${a.symbol}-${a.created_at}`} className="rounded-lg border border-red-500/30 bg-black/40 p-2" data-testid={`tv-alert-active-${a.symbol}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xl font-black text-white">{a.symbol}</span>
                    <span className={`font-mono text-lg font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                      {isUp ? "▲" : "▼"} {Math.abs(a.delta_pct).toFixed(2)}%
                    </span>
                    <span className="ml-auto text-[9px] uppercase tracking-widest text-slate-500">
                      {Math.round(a.age_seconds / 60)}m ago
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] leading-snug text-slate-200">{a.reason}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {/* Right column — Analyst Actions Today (top) + Archived alerts (below) */}
      <div className="flex flex-col gap-3 overflow-hidden">
        {/* Analyst Actions Today */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 via-slate-950 to-slate-950 p-3">
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-fuchsia-300" />
            <h3 className="text-[11px] font-black uppercase tracking-[0.24em] text-fuchsia-200">Analyst Actions · Today</h3>
            <span className="ml-auto rounded-full bg-fuchsia-500/20 px-2 py-0.5 text-[9px] font-black text-fuchsia-200">{analyst.length}</span>
          </div>
          {analyst.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <span className="text-xs text-slate-600">— no analyst shifts today —</span>
            </div>
          ) : (
            <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
              {analyst.slice(0, 8).map((a) => {
                const isUp = a.direction === "upgrade";
                return (
                  <li key={`${a.symbol}-${a.period}`} className={`flex items-center gap-2 rounded-md border ${isUp ? "border-emerald-500/25 bg-emerald-500/5" : "border-red-500/25 bg-red-500/5"} px-2 py-1.5`} data-testid={`tv-alert-analyst-${a.symbol}`}>
                    <span className="font-mono text-sm font-black text-white">{a.symbol}</span>
                    <span className={`rounded px-1 text-[9px] font-black uppercase ${isUp ? "bg-emerald-500/25 text-emerald-200" : "bg-red-500/25 text-red-200"}`}>
                      {isUp ? "▲ UP" : "▼ DOWN"}
                    </span>
                    <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500">{a.delta}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {/* Archived (older than 1h, within 24h) */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/40 p-3">
          <div className="mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            <h3 className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">Earlier Today</h3>
            <span className="ml-auto rounded-full bg-slate-800 px-2 py-0.5 text-[9px] font-black text-slate-400">{archived.length}</span>
          </div>
          {archived.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <span className="text-xs text-slate-600">— no earlier alerts —</span>
            </div>
          ) : (
            <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
              {archived.slice(0, 6).map((a) => {
                const isUp = a.delta_pct > 0;
                return (
                  <li key={`${a.symbol}-${a.created_at}`} className="flex items-center gap-2 rounded-md bg-slate-900/50 px-2 py-1.5" data-testid={`tv-alert-archived-${a.symbol}`}>
                    <span className="font-mono text-sm font-black text-white">{a.symbol}</span>
                    <span className={`font-mono text-sm font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                      {isUp ? "▲" : "▼"} {Math.abs(a.delta_pct).toFixed(2)}%
                    </span>
                    <span className="ml-auto text-[9px] uppercase text-slate-600">{Math.round(a.age_seconds / 60)}m</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

/* Small top-right floating banner that appears when a NEW sudden alert lands.
   Auto-dismisses after 8 seconds; user can also click X. */
const SuddenAlertBanner = ({ alert, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 8000);
    return () => clearTimeout(t);
  }, [alert, onClose]);
  const isUp = alert.delta_pct > 0;
  return (
    <div
      className={`fixed right-6 top-24 z-[200] w-96 rounded-2xl border-2 p-3 shadow-2xl backdrop-blur ${
        isUp ? "border-emerald-500/70 bg-emerald-950/85" : "border-red-500/70 bg-red-950/85"
      } animate-alert-slide-in`}
      data-testid="tv-sudden-alert-banner"
    >
      <div className="flex items-center gap-2">
        <Bell className={`h-5 w-5 ${isUp ? "text-emerald-300" : "text-red-300"} animate-pulse`} />
        <span className="text-[10px] font-black uppercase tracking-[0.28em] text-white/80">Sudden Move</span>
        <span className="ml-auto font-mono text-2xl font-black text-white">{alert.symbol}</span>
        <span className={`font-mono text-2xl font-black ${isUp ? "text-emerald-300" : "text-red-300"}`}>
          {isUp ? "▲" : "▼"} {Math.abs(alert.delta_pct).toFixed(2)}%
        </span>
      </div>
      <p className="mt-1.5 text-[11px] leading-snug text-white/90">{alert.reason}</p>
    </div>
  );
};

/* ============================================================================
   Hero & Villain of the Day — biggest gainer & loser with AI blurb
   ============================================================================ */
const HeroVillainSlot = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/falcon-view/hero-villain")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ ok: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="flex h-full items-center justify-center text-slate-500">Crowning today&apos;s hero &amp; villain…</div>;
  if (!data?.ok) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500">
      <div>
        <Crown className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">Waiting for portfolio movement…</div>
        <div className="mt-1 text-xs text-slate-600">(no gainers/losers to crown yet)</div>
      </div>
    </div>
  );

  const { hero, villain } = data;

  return (
    <div className="grid h-full grid-cols-2 gap-4" data-testid="tv-hero-villain-slot">
      {/* HERO */}
      <div className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-500/20 via-emerald-900/20 to-slate-950 p-6">
        {/* Confetti-ish flame accents */}
        <Flame className="absolute -right-2 -top-2 h-24 w-24 text-emerald-500/20" />
        <Flame className="absolute -bottom-4 -left-4 h-32 w-32 text-emerald-500/10 rotate-45" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/30 ring-2 ring-emerald-400">
            <Crown className="h-8 w-8 text-emerald-200" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.32em] text-emerald-300">Hero of the Day</div>
            <div className="text-sm text-emerald-400/70">A round of applause, please 🎉</div>
          </div>
        </div>
        <div className="relative mt-6 flex items-baseline gap-4">
          <div className="font-mono text-7xl font-black text-white" data-testid="tv-hero-symbol">{hero.symbol}</div>
          {hero.price != null && (
            <div className="font-mono text-3xl font-bold text-slate-200">${hero.price.toFixed(2)}</div>
          )}
        </div>
        <div className="relative mt-2 font-mono text-6xl font-black text-emerald-400">
          ▲ {Math.abs(hero.change_pct).toFixed(2)}%
        </div>
        <div className="relative mt-auto rounded-xl bg-black/40 p-3 backdrop-blur">
          <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-emerald-300">AI · Why it ripped</div>
          <div className="text-sm leading-snug text-emerald-100">{hero.blurb}</div>
        </div>
      </div>

      {/* VILLAIN */}
      <div className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-red-500/60 bg-gradient-to-br from-red-500/20 via-red-900/20 to-slate-950 p-6">
        <Skull className="absolute -right-2 -top-2 h-24 w-24 text-red-500/20" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/30 ring-2 ring-red-400">
            <Skull className="h-8 w-8 text-red-200" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.32em] text-red-300">Villain of the Day</div>
            <div className="text-sm text-red-400/70">Someone&apos;s stinking up the room 💀</div>
          </div>
        </div>
        <div className="relative mt-6 flex items-baseline gap-4">
          <div className="font-mono text-7xl font-black text-white" data-testid="tv-villain-symbol">{villain.symbol}</div>
          {villain.price != null && (
            <div className="font-mono text-3xl font-bold text-slate-200">${villain.price.toFixed(2)}</div>
          )}
        </div>
        <div className="relative mt-2 font-mono text-6xl font-black text-red-400">
          ▼ {Math.abs(villain.change_pct).toFixed(2)}%
        </div>
        <div className="relative mt-auto rounded-xl bg-black/40 p-3 backdrop-blur">
          <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-red-300">AI · Why it tanked</div>
          <div className="text-sm leading-snug text-red-100">{villain.blurb}</div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   FLASH OVERLAY — full-screen takeover when portfolio stock moves ≥ 5%
   ============================================================================ */
const FlashOverlay = ({ alert, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [alert, onDone]);
  const isUp = alert.direction === "UP";
  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center ${
        isUp ? "bg-emerald-500/30" : "bg-red-500/30"
      } animate-flash`}
      data-testid="tv-flash-overlay"
    >
      <div className="rounded-3xl border-4 border-white/40 bg-black/60 p-16 text-center backdrop-blur-xl">
        <div className="mb-4 flex items-center justify-center gap-4">
          {isUp ? <TrendingUp className="h-24 w-24 text-emerald-300" /> : <TrendingDown className="h-24 w-24 text-red-300" />}
        </div>
        <div className={`text-8xl font-black tracking-tight ${isUp ? "text-emerald-300" : "text-red-300"}`}>
          {alert.symbol}
        </div>
        <div className={`mt-4 font-mono text-9xl font-black ${isUp ? "text-emerald-400" : "text-red-400"}`}>
          {isUp ? "▲" : "▼"} {Math.abs(alert.change_pct).toFixed(2)}%
        </div>
        <div className="mt-6 text-2xl font-black uppercase tracking-widest text-white">
          {alert.symbol} · {isUp ? "BREAKING OUT" : "BREAKING DOWN"}
        </div>
        <div className="mt-2 font-mono text-lg text-slate-300">
          Last: ${alert.price?.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   SIDE RAIL DEFAULT (overridable via /api/falcon-view/config)
   ============================================================================ */
const DEFAULT_SIDE_RAIL = [
  { symbol: "FOREXCOM:DJI",         name: "DOW JONES",     group: "INDEX",     enabled: true },
  { symbol: "FOREXCOM:NSXUSD",      name: "NASDAQ 100",    group: "INDEX",     enabled: true },
  { symbol: "FOREXCOM:SPXUSD",      name: "S&P 500",       group: "INDEX",     enabled: true },
  { symbol: "TVC:GOLD",             name: "GOLD",          group: "COMMODITY", enabled: true },
  { symbol: "TVC:SILVER",           name: "SILVER",        group: "COMMODITY", enabled: true },
  { symbol: "TVC:USOIL",            name: "CRUDE OIL",     group: "COMMODITY", enabled: true },
  { symbol: "COINBASE:BTCUSD",      name: "BITCOIN",       group: "CRYPTO",    enabled: true },
  { symbol: "COINBASE:ETHUSD",      name: "ETHEREUM",      group: "CRYPTO",    enabled: true },
  { symbol: "COINBASE:SOLUSD",      name: "SOLANA",        group: "CRYPTO",    enabled: true },
];

/* ============================================================================
   NEWS TAKEOVER SLOTS — pre / after / off-hour focus
   ============================================================================ */

// A) MOVERS with reason — top gappers + headline + AI "why moved"
const NewsMoversSlot = ({ session = "open" }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get(`/falcon-view/news/movers?session=${session}&limit=8`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 90 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [session]);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Scanning market movers…</div>;
  const movers = data.movers || [];
  if (movers.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500" data-testid="tv-news-movers-slot">
      <div>
        <Zap className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">No standout movers right now.</div>
        <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-600">Session: {session}</div>
      </div>
    </div>
  );
  return (
    <div className="grid h-full grid-cols-2 gap-3" data-testid="tv-news-movers-slot">
      {movers.slice(0, 8).map((m) => {
        const up = m.side === "up" || (Number(m.change_pct) || 0) >= 0;
        return (
          <div
            key={m.symbol}
            className={`flex items-start gap-3 rounded-2xl border p-4 ${
              up ? "border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-slate-950 to-slate-950"
                 : "border-red-500/40 bg-gradient-to-r from-red-500/10 via-slate-950 to-slate-950"
            }`}
            data-testid={`tv-news-mover-${m.symbol}`}
          >
            <div className="flex min-w-[110px] flex-col">
              <span className="font-mono text-2xl font-black text-white">{m.symbol}</span>
              <span className={`font-mono text-lg font-black ${up ? "text-emerald-300" : "text-red-300"}`}>
                {up ? "+" : ""}{(Number(m.change_pct) || 0).toFixed(2)}%
              </span>
              <span className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-500">
                ${(Number(m.price) || 0).toFixed(2)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold leading-snug text-slate-100 line-clamp-2">
                {m.headline || <span className="italic text-slate-500">No headline linked yet.</span>}
              </div>
              {m.ai_reason && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/10 px-2 py-1.5" data-testid={`tv-news-mover-ai-${m.symbol}`}>
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-300" />
                  <span className="text-[11px] leading-snug text-amber-100">{m.ai_reason}</span>
                </div>
              )}
              <div className="mt-1 flex items-center gap-2 text-[9px] uppercase tracking-widest text-slate-600">
                <span>{m.headline_source || "—"}</span>
                {m.headline_time && <span>· {m.headline_time}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// B) NEWS WALL — my stocks headline scroll
const NewsWallSlot = () => {
  const [data, setData] = useState(null);
  const [fallback, setFallback] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/news/wall");
        if (cancelled) return;
        setData(r.data);
        // If wall is empty, hydrate from /news/general (proven-working AI briefs).
        if (!(r.data?.headlines || []).length) {
          try {
            const g = await api.get("/falcon-view/news/general");
            if (!cancelled) setFallback(g.data);
          } catch { /* ignore */ }
        }
      } catch {
        // /news/wall failed entirely — use /news/general so slot always populates.
        try {
          const g = await api.get("/falcon-view/news/general");
          if (!cancelled) setFallback(g.data);
        } catch { /* ignore */ }
      }
    };
    load();
    const t = setInterval(load, 120 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Prefer wall headlines; else stitch business + political briefs together.
  let items = (data?.headlines || []).slice(0, 14);
  if (items.length === 0 && fallback) {
    const biz = fallback.business || [];
    const pol = fallback.political || [];
    items = [...biz.slice(0, 4), ...pol.slice(0, 4)].map((h) => ({
      title: h.title,
      source: h.source,
      published_relative: h.published_relative,
      summary: h.summary,
      ai_expanded: h.ai_expanded,
    }));
  }

  // Auto-skip the slide if BOTH sources returned nothing (rather than stalling).
  useSkipIfEmpty(!!(data || fallback), items.length === 0);

  if (!data && !fallback) {
    return <div className="flex h-full items-center justify-center text-slate-500">Pulling headlines…</div>;
  }
  if (items.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500" data-testid="tv-news-wall-slot">
      <div>
        <Radio className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">No headlines available.</div>
      </div>
    </div>
  );
  return (
    <div className="flex h-full flex-col" data-testid="tv-news-wall-slot">
      <div className="mb-2 flex items-center justify-between rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-cyan-300" />
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-cyan-200">My Stocks · News Wall</span>
        </div>
        <span className="font-mono text-[10px] text-cyan-300/70">{items.length} live · covering {(data.symbols || []).length} tickers</span>
      </div>
      <div className="grid grid-cols-2 gap-2 overflow-hidden">
        {items.map((h, idx) => {
          const senti = (h.sentiment || "").toLowerCase();
          const senti_color = senti === "positive" ? "text-emerald-300" : senti === "negative" ? "text-red-300" : "text-slate-400";
          return (
            <div key={idx} className="flex items-start gap-2 rounded-lg border border-slate-800 bg-slate-950/50 p-2.5">
              <span className={`min-w-[56px] rounded-md px-1.5 py-0.5 text-center font-mono text-xs font-black ${senti === "positive" ? "bg-emerald-500/15 text-emerald-300" : senti === "negative" ? "bg-red-500/15 text-red-300" : "bg-slate-800 text-slate-300"}`}>
                {h.symbol}
              </span>
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-[12px] font-semibold leading-snug text-slate-100">{h.title}</div>
                <div className={`mt-0.5 flex items-center gap-1.5 text-[9px] uppercase tracking-widest ${senti_color}`}>
                  <span>{h.source}</span>
                  {h.published_relative && <span>· {h.published_relative}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// C) EARNINGS CATALYST BOARD
const EarningsCatalystSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/news/earnings")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500" data-testid="tv-earnings-slot">Loading earnings board…</div>;

  const owned = data.owned_earnings || [];
  const major = data.major_earnings || [];
  const HOUR_LABEL = { bmo: "BMO", amc: "AMC", dmh: "DMH" };

  const renderTile = (e) => {
    const eps_beat = e.eps_verdict === "BEAT";
    const eps_miss = e.eps_verdict === "MISS";
    const rev_beat = e.revenue_verdict === "BEAT";
    const rev_miss = e.revenue_verdict === "MISS";
    const reaction = Number(e.reaction_pct) || 0;
    const reaction_up = reaction >= 0;
    return (
      <div key={`${e.symbol}-${e.date}`} className={`rounded-lg border p-2 ${eps_beat || rev_beat ? "border-emerald-500/30" : eps_miss || rev_miss ? "border-red-500/30" : "border-slate-800"} bg-slate-950/60`} data-testid={`tv-earnings-${e.symbol}`}>
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-base font-black text-white">{e.symbol}</span>
            <span className="rounded-full bg-slate-800 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-slate-400">
              {HOUR_LABEL[e.hour] || "—"}
            </span>
          </div>
          {e.reported && (
            <span className={`font-mono text-[11px] font-black ${reaction_up ? "text-emerald-300" : "text-red-300"}`}>
              {reaction_up ? "+" : ""}{reaction.toFixed(2)}%
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-1 text-[9px]">
          <div className={`rounded px-1 py-0.5 ${eps_beat ? "bg-emerald-500/15 text-emerald-200" : eps_miss ? "bg-red-500/15 text-red-200" : "bg-slate-900 text-slate-400"}`}>
            <div className="text-[7px] uppercase tracking-widest">EPS · {e.eps_verdict}</div>
            <div className="font-mono">
              {e.eps_actual !== null && e.eps_actual !== undefined ? Number(e.eps_actual).toFixed(2) : "—"}
              <span className="text-slate-500"> / {e.eps_estimate !== null && e.eps_estimate !== undefined ? Number(e.eps_estimate).toFixed(2) : "—"}</span>
            </div>
          </div>
          <div className={`rounded px-1 py-0.5 ${rev_beat ? "bg-emerald-500/15 text-emerald-200" : rev_miss ? "bg-red-500/15 text-red-200" : "bg-slate-900 text-slate-400"}`}>
            <div className="text-[7px] uppercase tracking-widest">REV · {e.revenue_verdict}</div>
            <div className="font-mono">
              {e.revenue_actual ? `$${(Number(e.revenue_actual) / 1e9).toFixed(2)}B` : "—"}
              <span className="text-slate-500"> / {e.revenue_estimate ? `$${(Number(e.revenue_estimate) / 1e9).toFixed(2)}B` : "—"}</span>
            </div>
          </div>
        </div>
        {!e.reported && (
          <div className="mt-1 text-[8px] uppercase tracking-widest text-amber-300">Awaiting release</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col" data-testid="tv-earnings-slot">
      <div className="mb-2 flex items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-300" />
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-amber-200">Earnings · Yours vs Majors · Actual vs Estimate</span>
        </div>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 overflow-hidden">
        {/* YOURS */}
        <div className="flex flex-col overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-lg">💼</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-200">Your Book · This Week</h3>
            <span className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-black text-emerald-200">{owned.length}</span>
          </div>
          {owned.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-center text-xs text-slate-500">
              — no earnings on your holdings this window —
            </div>
          ) : (
            <div className="grid flex-1 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
              {owned.slice(0, 8).map(renderTile)}
            </div>
          )}
        </div>
        {/* MAJORS */}
        <div className="flex flex-col overflow-hidden rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-lg">🏆</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-200">Major Names · Market-Moving</h3>
            <span className="ml-auto rounded-full bg-cyan-500/20 px-2 py-0.5 text-[9px] font-black text-cyan-200">{major.length}</span>
          </div>
          {major.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-center text-xs text-slate-500">
              — no major earnings in window —
            </div>
          ) : (
            <div className="grid flex-1 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
              {major.slice(0, 8).map(renderTile)}
            </div>
          )}
        </div>
      </div>
      {/* Golden rule footer */}
      <div className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5">
        <span className="text-base">⚠️</span>
        <span className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-200">
          Rule · Never Trade Earnings — Wait For The Reaction, Trade The Follow-Through
        </span>
      </div>
    </div>
  );
};

// D) UPGRADES / DOWNGRADES — analyst consensus board
const UpgradesDowngradesSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/news/upgrades-downgrades")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Checking analyst actions…</div>;
  const actions = data.actions || [];
  if (actions.length === 0) return (
    <div className="flex h-full items-center justify-center text-slate-500" data-testid="tv-updowns-slot">
      No recent analyst actions on your book.
    </div>
  );
  return (
    <div className="flex h-full flex-col" data-testid="tv-updowns-slot">
      <div className="mb-2 flex items-center justify-between rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-fuchsia-300" />
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-fuchsia-200">Analyst Board · Consensus & Shifts</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 overflow-hidden">
        {actions.slice(0, 10).map((a) => {
          const isUp = a.action === "upgrade";
          const isDown = a.action === "downgrade";
          const border = isUp ? "border-emerald-500/40" : isDown ? "border-red-500/40" : "border-slate-800";
          const chipColor = a.consensus === "STRONG BUY"
            ? "bg-emerald-500/25 text-emerald-200"
            : a.consensus === "BUY"
              ? "bg-emerald-500/15 text-emerald-300"
              : a.consensus === "SELL"
                ? "bg-red-500/15 text-red-300"
                : "bg-slate-800 text-slate-300";
          return (
            <div key={a.symbol} className={`rounded-lg border ${border} bg-slate-950/60 p-2.5`} data-testid={`tv-updown-${a.symbol}`}>
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-black text-white">{a.symbol}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase ${chipColor}`}>
                    {a.consensus}
                  </span>
                </div>
                <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase ${isUp ? "bg-emerald-500/25 text-emerald-200" : isDown ? "bg-red-500/25 text-red-200" : "bg-slate-800 text-slate-400"}`}>
                  {isUp ? "▲ SHIFT UP" : isDown ? "▼ SHIFT DOWN" : "· HOLDING"}
                </span>
              </div>
              <div className="flex items-center gap-1 text-[10px] font-mono">
                <span className="rounded bg-emerald-500/25 px-1 text-emerald-100">SB {a.strong_buy}</span>
                <span className="rounded bg-emerald-500/15 px-1 text-emerald-200">B {a.buy}</span>
                <span className="rounded bg-slate-800 px-1 text-slate-300">H {a.hold}</span>
                <span className="rounded bg-red-500/15 px-1 text-red-200">S {a.sell}</span>
                <span className="rounded bg-red-500/25 px-1 text-red-100">SS {a.strong_sell}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[9px] uppercase tracking-widest">
                <span className="text-slate-500">{a.total_analysts} analysts · period {a.period}</span>
                {a.delta && <span className={isUp ? "text-emerald-300" : "text-red-300"}>{a.delta}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// E) AI DAILY BRIEF — 3-bullet gameplan
const AIBriefSlot = ({ session = "open" }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get(`/falcon-view/news/daily-brief?session=${session}`)
      .then((r) => { if (!cancelled) { setData(r.data); setFailed(false); } })
      .catch(() => { if (!cancelled) { setData({ bullets: [] }); setFailed(true); } });
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [session]);
  const bullets = data?.bullets || [];
  // Auto-skip when we have a definitive empty response — prevents forever
  // stuck "Synthesizing today's edge…" loader.
  useSkipIfEmpty(!!data, bullets.length === 0);
  const label = { pre: "PRE-MARKET GAMEPLAN", open: "TRADING DAY GAMEPLAN", after: "AFTER-HOURS DEBRIEF", closed: "OVERNIGHT DEBRIEF" };
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-8" data-testid="tv-ai-brief-slot">
      <div className="flex items-center gap-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent px-4 py-2">
        <Sparkles className="h-5 w-5 text-amber-300" />
        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-200">
          {label[session] || "AI BRIEF"} · Powered by Claude
        </span>
      </div>
      {!data && <div className="text-slate-500">Synthesizing today&apos;s edge…</div>}
      {data && bullets.length === 0 && failed && (
        <div className="text-slate-500">Brief unavailable — skipping slide…</div>
      )}
      {data && bullets.length > 0 && (
        <div className="grid w-full max-w-5xl grid-cols-1 gap-3">
          {bullets.map((b, i) => (
            <div key={i} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-5" data-testid={`tv-ai-brief-bullet-${i}`}>
              <div className="mb-1.5 flex items-center gap-2">
                <span className={`h-2 w-8 rounded-full ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-cyan-400" : "bg-emerald-400"}`} />
                <span className="text-[10px] font-black uppercase tracking-[0.32em] text-slate-400">{b.tag}</span>
              </div>
              <div className="text-lg leading-snug text-slate-100">{b.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// I) WEEKLY SUMMARY — weekend-safe recap of the past 5 sessions
const WeeklySummarySlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/weekly-summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500" data-testid="tv-weekly-summary-slot">Building this week&apos;s recap…</div>;
  const port = data.portfolio || {};
  const ira = data.ira || {};
  const portUp = (port.pct_change || 0) >= 0;
  const iraUp = (ira.pct_change || 0) >= 0;
  const gainers = data.top_gainers || [];
  const losers = data.top_losers || [];

  return (
    <div className="flex h-full flex-col gap-3" data-testid="tv-weekly-summary-slot">
      {/* Header — Portfolio + IRA week P&L */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-2xl border p-4 ${portUp ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950" : "border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950"}`}>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-lg">💼</span>
            <span className="text-[10px] font-black uppercase tracking-[0.32em] text-slate-300">Portfolio · Week</span>
          </div>
          <div className={`font-mono text-4xl font-black ${portUp ? "text-emerald-300" : "text-red-300"}`} data-testid="tv-weekly-port-pct">
            {portUp ? "+" : ""}{(port.pct_change || 0).toFixed(2)}%
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>{portUp ? "▲" : "▼"} {port.winners_count || 0} winners · {port.losers_count || 0} losers</span>
          </div>
        </div>
        <div className={`rounded-2xl border p-4 ${iraUp ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950" : "border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950"}`}>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-lg">🏦</span>
            <span className="text-[10px] font-black uppercase tracking-[0.32em] text-slate-300">IRA · Week</span>
          </div>
          <div className={`font-mono text-4xl font-black ${iraUp ? "text-emerald-300" : "text-red-300"}`} data-testid="tv-weekly-ira-pct">
            {iraUp ? "+" : ""}{(ira.pct_change || 0).toFixed(2)}%
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>{iraUp ? "▲" : "▼"} {ira.winners_count || 0} winners · {ira.losers_count || 0} losers</span>
          </div>
        </div>
      </div>

      {/* Top gainers / losers grid */}
      <div className="grid flex-1 grid-cols-2 gap-3 overflow-hidden">
        <div className="flex flex-col overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-300" />
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-200">Top Gainers · This Week</h3>
          </div>
          <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
            {gainers.slice(0, 6).map((h) => (
              <li key={h.symbol} className="flex items-center gap-2 rounded-md border border-emerald-500/25 bg-black/40 px-2 py-1.5" data-testid={`tv-weekly-gainer-${h.symbol}`}>
                <span className="font-mono text-sm font-black text-white">{h.symbol}</span>
                {h.company_name && <span className="truncate text-[10px] text-slate-500">{h.company_name}</span>}
                <span className="ml-auto font-mono text-sm font-black text-emerald-300">+{(h.change_pct || 0).toFixed(2)}%</span>
              </li>
            ))}
            {gainers.length === 0 && <div className="text-xs text-slate-600">— no data —</div>}
          </ul>
        </div>
        <div className="flex flex-col overflow-hidden rounded-2xl border border-red-500/30 bg-red-500/5 p-3">
          <div className="mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-300" />
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-red-200">Top Losers · This Week</h3>
          </div>
          <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
            {losers.slice(0, 6).map((h) => (
              <li key={h.symbol} className="flex items-center gap-2 rounded-md border border-red-500/25 bg-black/40 px-2 py-1.5" data-testid={`tv-weekly-loser-${h.symbol}`}>
                <span className="font-mono text-sm font-black text-white">{h.symbol}</span>
                {h.company_name && <span className="truncate text-[10px] text-slate-500">{h.company_name}</span>}
                <span className="ml-auto font-mono text-sm font-black text-red-300">{(h.change_pct || 0).toFixed(2)}%</span>
              </li>
            ))}
            {losers.length === 0 && <div className="text-xs text-slate-600">— no data —</div>}
          </ul>
        </div>
      </div>

      <div className="text-center text-[9px] uppercase tracking-widest text-slate-600">
        {data.week_start} → {data.week_end} · re-checks every 15 min
      </div>
    </div>
  );
};

// 🔥 SHORT SQUEEZE — Finviz "Float Short > 30%" screener with squeeze score
const ShortSqueezeSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/short-squeeze")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30 * 60 * 1000); // 30-min re-poll (data itself refreshes daily)
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const candidates = (data?.candidates || []).slice(0, 8);
  useSkipIfEmpty(!!data, candidates.length === 0);
  const labelStyle = (lbl) => {
    if (lbl === "POWDER KEG") return "border-red-500/70 bg-gradient-to-br from-red-600/25 via-slate-950 to-slate-950 text-red-200 shadow-[0_0_24px_rgba(220,38,38,0.25)]";
    if (lbl === "LOADED") return "border-orange-500/60 bg-gradient-to-br from-orange-500/20 via-slate-950 to-slate-950 text-orange-200";
    if (lbl === "WATCH") return "border-amber-500/50 bg-gradient-to-br from-amber-500/15 via-slate-950 to-slate-950 text-amber-200";
    return "border-slate-700/60 bg-slate-900/40 text-slate-300";
  };
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500" data-testid="tv-short-squeeze-slot">Scanning Finviz for short squeeze setups…</div>;
  return (
    <div className="flex h-full flex-col gap-3" data-testid="tv-short-squeeze-slot">
      {/* Header */}
      <div className="flex items-center justify-between rounded-2xl border border-red-500/40 bg-gradient-to-r from-red-600/20 via-orange-500/10 to-slate-950 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🔥</span>
          <div>
            <h2 className="text-lg font-black uppercase tracking-[0.24em] text-white">Short Squeeze Radar</h2>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Finviz · Float Short &gt; 30% · Squeeze-scored</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-black text-red-300" data-testid="tv-squeeze-qualifying">{data.qualifying || 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500">qualifying · {data.scanned || 0} scanned</div>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid flex-1 grid-cols-4 grid-rows-2 gap-2.5 overflow-hidden">
        {candidates.map((c) => (
          <div
            key={c.symbol}
            className={`flex flex-col justify-between overflow-hidden rounded-2xl border p-3 ${labelStyle(c.squeeze_label)}`}
            data-testid={`tv-squeeze-card-${c.symbol}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-2xl font-black leading-none tracking-tight">{c.symbol}</div>
                <div className="mt-1 text-[9px] uppercase tracking-widest text-slate-500">{c.market_cap || "—"}</div>
              </div>
              <span className="rounded-md border border-current/40 bg-black/40 px-1.5 py-0.5 font-mono text-xs font-black">
                {c.squeeze_score?.toFixed?.(0) ?? Math.round(c.squeeze_score || 0)}
              </span>
            </div>

            <div className="my-2 grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-[10px] leading-tight text-slate-300">
              <span>SF</span><span className="text-right text-white">{(c.short_float_pct || 0).toFixed(1)}%</span>
              <span>DTC</span><span className="text-right text-white">{(c.days_to_cover || 0).toFixed(1)}d</span>
              <span>W%</span>
              <span className={`text-right ${(c.perf_week_pct || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {(c.perf_week_pct || 0) >= 0 ? "+" : ""}{(c.perf_week_pct || 0).toFixed(1)}%
              </span>
              <span>RV</span><span className="text-right text-white">{(c.rel_volume || 0).toFixed(2)}x</span>
            </div>

            <div className="rounded-md border border-current/40 bg-black/50 py-1 text-center text-[10px] font-black uppercase tracking-widest">
              {c.squeeze_label}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center text-[9px] uppercase tracking-widest text-slate-600">
        POWDER KEG ≥85 · LOADED ≥75 · WATCH ≥65 · refreshes daily from Finviz
      </div>
    </div>
  );
};

// J) BUSINESS NEWS — TV-friendly detailed cards (fewer, richer, longer read time)
const BusinessNewsSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/news/general")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  // 3 detailed cards per slide so summaries breathe. Rotates through top 12 across polls.
  const items = (data?.business || []).slice(0, 3);
  useSkipIfEmpty(!!data, items.length === 0);
  return (
    <div className="flex h-full flex-col" data-testid="tv-business-news-slot">
      <div className="mb-3 flex items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">💼</span>
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-amber-200">Business & Markets · Detailed Brief</span>
        </div>
        <span className="font-mono text-[10px] text-amber-300/70">{items.length} stories · re-poll 3m</span>
      </div>
      {!data && <div className="flex h-full items-center justify-center text-slate-500">Pulling business wire…</div>}
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-hidden">
        {items.map((h, idx) => (
          <div key={idx} className="flex gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4" data-testid={`tv-business-${idx}`}>
            <div className="flex min-w-[80px] flex-col items-center justify-center rounded-lg bg-amber-500/15 px-2 py-2 text-amber-200">
              <div className="text-[10px] font-black uppercase tracking-widest">{h.source?.slice(0, 12) || "WIRE"}</div>
              {h.published_relative && <div className="mt-0.5 text-[10px] text-amber-300">{h.published_relative}</div>}
              {h.ai_expanded && <div className="mt-1 rounded-full bg-amber-500/25 px-1.5 py-0.5 text-[8px] font-black text-amber-100">AI BRIEF</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xl font-bold leading-snug text-slate-100">{h.title}</div>
              {h.summary && (
                <div className="mt-2 text-base leading-relaxed text-slate-300">{h.summary}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// K) POLITICAL NEWS — TV-friendly detailed cards
const PoliticalNewsSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/news/general")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 3 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const items = (data?.political || []).slice(0, 3);
  useSkipIfEmpty(!!data, items.length === 0);
  return (
    <div className="flex h-full flex-col" data-testid="tv-political-news-slot">
      <div className="mb-3 flex items-center justify-between rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">🏛️</span>
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-blue-200">Political & Geopolitical · Detailed Brief</span>
        </div>
        <span className="font-mono text-[10px] text-blue-300/70">{items.length} stories · re-poll 3m</span>
      </div>
      {!data && <div className="flex h-full items-center justify-center text-slate-500">Pulling political wire…</div>}
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-hidden">
        {items.map((h, idx) => (
          <div key={idx} className="flex gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4" data-testid={`tv-political-${idx}`}>
            <div className="flex min-w-[80px] flex-col items-center justify-center rounded-lg bg-blue-500/15 px-2 py-2 text-blue-200">
              <div className="text-[10px] font-black uppercase tracking-widest">{h.source?.slice(0, 12) || "WIRE"}</div>
              {h.published_relative && <div className="mt-0.5 text-[10px] text-blue-300">{h.published_relative}</div>}
              {h.ai_expanded && <div className="mt-1 rounded-full bg-blue-500/25 px-1.5 py-0.5 text-[8px] font-black text-blue-100">AI BRIEF</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xl font-bold leading-snug text-slate-100">{h.title}</div>
              {h.summary && (
                <div className="mt-2 text-base leading-relaxed text-slate-300">{h.summary}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================================================================
   BREAKING NEWS OVERLAY — 24/7 flash bar that pauses whatever slot is on
   ============================================================================ */
const BreakingNewsOverlay = () => {
  const [alerts, setAlerts] = useState([]);
  const [visible, setVisible] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const seenIds = useRef(new Set());

  // Poll breaking news every 45s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/breaking-news");
        if (cancelled) return;
        const fresh = r.data?.alerts || [];
        // Detect brand-new alerts we haven't shown before
        const brandNew = fresh.filter((a) => {
          const key = `${a.symbol || "MKT"}|${(a.title || "").slice(0, 60)}`;
          if (seenIds.current.has(key)) return false;
          seenIds.current.add(key);
          return true;
        });
        if (brandNew.length > 0) {
          setAlerts(brandNew);
          setCurrentIdx(0);
          setVisible(true);
          // Auto-dismiss after 10s per alert
          setTimeout(() => setVisible(false), 10_000);
        }
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!visible || alerts.length === 0) return null;
  const cur = alerts[currentIdx % alerts.length];
  const isHigh = cur.urgency === "high";

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-8 backdrop-blur-md ${isHigh ? "bg-red-950/85" : "bg-amber-950/85"}`}
      data-testid="tv-breaking-overlay"
    >
      <div className={`w-full max-w-6xl rounded-3xl border-4 ${isHigh ? "border-red-500 shadow-[0_0_120px_rgba(239,68,68,0.5)]" : "border-amber-500 shadow-[0_0_120px_rgba(245,158,11,0.5)]"} bg-black/70 p-10 animate-pulse-slow`}>
        <div className="mb-4 flex items-center gap-4">
          <span className={`rounded-full px-4 py-2 text-lg font-black uppercase tracking-[0.4em] ${isHigh ? "bg-red-500 text-black" : "bg-amber-400 text-black"}`}>
            {isHigh ? "🚨 BREAKING" : "⚡ MACRO ALERT"}
          </span>
          {cur.symbol && (
            <span className="font-mono text-4xl font-black text-white">{cur.symbol}</span>
          )}
          <span className="ml-auto text-[11px] uppercase tracking-widest text-slate-400">
            {cur.published_relative || "just now"} · {cur.source || ""}
          </span>
        </div>
        <div className="text-4xl font-black leading-tight text-white" data-testid="tv-breaking-title">
          {cur.title}
        </div>
        <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-500">
          <span>this alert dismisses in ~10s · falcon view · sp wealth</span>
          {alerts.length > 1 && <span className="ml-auto">{currentIdx + 1} / {alerts.length}</span>}
        </div>
      </div>
    </div>
  );
};



// G) WORLD MARKETS — Asia + Europe indices to pre-plan US session
const WorldMarketsSlot = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/world-markets")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Pinging Asian & European desks…</div>;

  const renderTile = (m) => {
    const cp = Number(m.change_pct) || 0;
    const up = cp >= 0;
    return (
      <div key={m.symbol} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${up ? "border-emerald-500/25 bg-emerald-500/5" : "border-red-500/25 bg-red-500/5"}`} data-testid={`tv-world-${m.symbol}`}>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-base font-black text-white">{m.name}</span>
            <span className="text-[9px] uppercase tracking-widest text-slate-500">{m.region}</span>
          </div>
          <div className="font-mono text-xs text-slate-400">
            {m.price !== null && m.price !== undefined
              ? Number(m.price).toLocaleString(undefined, {maximumFractionDigits: m.price < 100 ? 4 : 2})
              : "—"}
          </div>
        </div>
        <div className={`font-mono text-lg font-black ${up ? "text-emerald-300" : "text-red-300"}`}>
          {up ? "▲" : "▼"} {Math.abs(cp).toFixed(2)}%
        </div>
      </div>
    );
  };

  const asiaBadge = data.asia_session === "live"
    ? <span className="rounded-full bg-emerald-500/25 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-200 animate-pulse">LIVE</span>
    : <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-slate-300">CLOSED</span>;
  const euBadge = data.europe_session === "live"
    ? <span className="rounded-full bg-emerald-500/25 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-200 animate-pulse">LIVE</span>
    : <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-slate-300">CLOSED</span>;

  return (
    <div className="grid h-full grid-cols-2 gap-4" data-testid="tv-world-markets-slot">
      {/* Asia */}
      <div className="flex flex-col gap-2 overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-500/5 via-slate-950 to-slate-950 p-4">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-2xl">🌏</span>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.32em] text-red-300">Asia Session</div>
            <div className="text-lg font-black text-white">Overnight Tone</div>
          </div>
          <div className="ml-auto">{asiaBadge}</div>
        </div>
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {(data.asia || []).map(renderTile)}
        </div>
      </div>
      {/* Europe */}
      <div className="flex flex-col gap-2 overflow-hidden rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/5 via-slate-950 to-slate-950 p-4">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-2xl">🌍</span>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.32em] text-blue-300">Europe Session</div>
            <div className="text-lg font-black text-white">Morning Setup</div>
          </div>
          <div className="ml-auto">{euBadge}</div>
        </div>
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {(data.europe || []).map(renderTile)}
        </div>
        {/* Currencies footer */}
        {data.currencies?.length > 0 && (
          <div className="mt-2 border-t border-slate-800 pt-2">
            <div className="mb-1 text-[9px] font-black uppercase tracking-widest text-slate-500">Currencies · Impact on US</div>
            <div className="grid grid-cols-2 gap-1.5">
              {data.currencies.slice(0, 4).map((c) => {
                const cp = Number(c.change_pct) || 0;
                const up = cp >= 0;
                return (
                  <div key={c.symbol} className="flex items-center justify-between rounded bg-slate-900/60 px-2 py-1 text-xs" data-testid={`tv-world-fx-${c.symbol}`}>
                    <span className="font-mono font-bold text-slate-200">{c.name}</span>
                    <span className={`font-mono font-black ${up ? "text-emerald-300" : "text-red-300"}`}>
                      {up ? "+" : ""}{cp.toFixed(2)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// H) INSTITUTIONAL FLOW — insider buys/sells + dark pool blocks
const InstitutionalFlowSlot = () => {
  const [data, setData] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/institutional-flow")
      .then((r) => { if (!cancelled) { setData(r.data); setRefreshedAt(new Date()); } })
      .catch(() => {});
    load();
    const t = setInterval(load, 60 * 1000);  // fresh every 60s
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Loading institutional & insider flow…</div>;

  const buys = data.insider_buys || [];
  const sells = data.insider_sells || [];
  const darkPool = data.dark_pool || [];
  const mktInsider = data.market_insider || [];

  const fmtMillions = (v) => {
    const n = Number(v) || 0;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };
  const fmtShares = (v) => {
    const n = Number(v) || 0;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M sh`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K sh`;
    return `${n} sh`;
  };

  return (
    <div className="flex h-full flex-col gap-2" data-testid="tv-institutional-slot">
      {/* Header */}
      <div className="flex items-center justify-between rounded-xl border border-violet-500/30 bg-violet-500/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">🏦</span>
          <span className="text-[10px] font-black uppercase tracking-[0.32em] text-violet-200">Institutional & Insider Flow · Always Fresh</span>
        </div>
        <span className="font-mono text-[10px] text-violet-300/70">
          {refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString()}` : ""} · re-poll 60s
        </span>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-3 overflow-hidden">
        {/* Insider Buys — bullish signal */}
        <div className="flex flex-col overflow-hidden rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950 p-3">
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-300" />
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-200">Insider BUYS · Form 4 (open market)</h3>
            <span className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-black text-emerald-200">{buys.length}</span>
          </div>
          {buys.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-xs text-slate-600">— no material insider buys yet —</div>
          ) : (
            <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
              {buys.slice(0, 6).map((b, i) => (
                <li key={`${b.symbol}-${i}`} className="rounded-md border border-emerald-500/25 bg-black/40 p-2" data-testid={`tv-inst-buy-${b.symbol}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-black text-white">{b.symbol}</span>
                    <span className="text-[10px] text-slate-400">{b.insider}</span>
                    <span className="ml-auto font-mono text-sm font-black text-emerald-300">{fmtMillions(b.value_usd)}</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-[10px] text-slate-500">
                    <span>{fmtShares(b.shares)} @ ${Number(b.price || 0).toFixed(2)}</span>
                    <span>{b.relative}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Insider Sells */}
        <div className="flex flex-col overflow-hidden rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-500/10 via-slate-950 to-slate-950 p-3">
          <div className="mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-300" />
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-red-200">Insider SELLS · Form 4 (open market)</h3>
            <span className="ml-auto rounded-full bg-red-500/20 px-2 py-0.5 text-[9px] font-black text-red-200">{sells.length}</span>
          </div>
          {sells.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-xs text-slate-600">— no material insider sells yet —</div>
          ) : (
            <ul className="flex-1 space-y-1.5 overflow-y-auto pr-1">
              {sells.slice(0, 6).map((s, i) => (
                <li key={`${s.symbol}-${i}`} className="rounded-md border border-red-500/25 bg-black/40 p-2" data-testid={`tv-inst-sell-${s.symbol}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-black text-white">{s.symbol}</span>
                    <span className="text-[10px] text-slate-400">{s.insider}</span>
                    <span className="ml-auto font-mono text-sm font-black text-red-300">{fmtMillions(s.value_usd)}</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-[10px] text-slate-500">
                    <span>{fmtShares(s.shares)} @ ${Number(s.price || 0).toFixed(2)}</span>
                    <span>{s.relative}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Dark pool + UW market-wide insider footer */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col rounded-xl border border-slate-800 bg-slate-950/60 p-2.5">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-lg">🕶️</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-300">Dark Pool Blocks</h3>
            <span className="ml-auto text-[9px] uppercase text-slate-600">{darkPool.length} prints</span>
          </div>
          {darkPool.length === 0 ? (
            <div className="text-[11px] text-slate-600">— no dark pool prints this cycle —</div>
          ) : (
            <ul className="grid grid-cols-2 gap-1">
              {darkPool.slice(0, 6).map((d, i) => (
                <li key={i} className="flex items-center justify-between rounded bg-slate-900/60 px-2 py-1 text-[11px]" data-testid={`tv-inst-dark-${d.symbol}-${i}`}>
                  <span className="font-mono font-bold text-white">{d.symbol}</span>
                  <span className="font-mono font-black text-slate-200">{fmtMillions(d.notional_usd)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col rounded-xl border border-slate-800 bg-slate-950/60 p-2.5">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-lg">🐋</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-300">Market-Wide Insider (UW)</h3>
            <span className="ml-auto text-[9px] uppercase text-slate-600">{mktInsider.length}</span>
          </div>
          {mktInsider.length === 0 ? (
            <div className="text-[11px] text-slate-600">— no market-wide activity captured —</div>
          ) : (
            <ul className="space-y-1">
              {mktInsider.slice(0, 4).map((m, i) => (
                <li key={i} className="flex items-center gap-2 rounded bg-slate-900/60 px-2 py-1 text-[11px]" data-testid={`tv-inst-uw-${m.symbol}-${i}`}>
                  <span className="font-mono font-bold text-white">{m.symbol}</span>
                  <span className={`rounded px-1 text-[9px] font-black ${m.side === "BUY" ? "bg-emerald-500/25 text-emerald-200" : "bg-red-500/25 text-red-200"}`}>
                    {m.side}
                  </span>
                  <span className="truncate text-[10px] text-slate-400">{m.insider}</span>
                  <span className="ml-auto font-mono font-black text-slate-200">{fmtMillions(m.value_usd)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};


// F) PLAY SUGGESTOR — flash slide when a real catalyst hits an active options play
const PlaySuggestorSlot = () => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/falcon-view/news/play-suggestor")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 90 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const flashes = data?.flashes || [];
  useEffect(() => {
    if (flashes.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % flashes.length), 6000);
    return () => clearInterval(t);
  }, [flashes.length]);

  // Skip when data is loaded and no flashes exist (never sit on "no catalysts" screen)
  useSkipIfEmpty(!!data, flashes.length === 0);

  if (!data) return <div className="flex h-full items-center justify-center text-slate-500">Scanning your plays for catalysts…</div>;
  if (flashes.length === 0) return (
    <div className="flex h-full items-center justify-center text-center text-slate-500" data-testid="tv-play-suggestor-slot">
      <div>
        <Target className="mx-auto mb-2 h-8 w-8 text-slate-600" />
        <div className="text-sm">No actionable catalysts on your plays right now.</div>
        <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-600">Scanned {data.total_plays_scanned || 0} plays · re-checks every 90s</div>
      </div>
    </div>
  );

  const cur = flashes[idx % flashes.length];
  const isGreen = cur.urgency === "green_flash" || cur.urgency === "green";
  const isFlash = cur.urgency === "green_flash" || cur.urgency === "red_flash";
  const pnlUp = (Number(cur.pnl_pct) || 0) >= 0;

  return (
    <div
      className={`flex h-full flex-col items-stretch justify-center gap-4 rounded-2xl border-2 p-6 ${
        isGreen
          ? "border-emerald-500/60 bg-gradient-to-br from-emerald-500/15 via-emerald-950/40 to-slate-950"
          : "border-red-500/60 bg-gradient-to-br from-red-500/15 via-red-950/40 to-slate-950"
      } ${isFlash ? "animate-pulse" : ""}`}
      data-testid="tv-play-suggestor-slot"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`rounded-lg px-3 py-1 text-[10px] font-black uppercase tracking-[0.32em] ${isGreen ? "bg-emerald-500/25 text-emerald-100" : "bg-red-500/25 text-red-100"}`}>
            {isFlash ? "PLAY FLASH" : "PLAY WATCH"}
          </span>
          <div className="flex flex-wrap gap-1">
            {(cur.source_tags || []).map((s) => (
              <span key={s} className="rounded-full border border-slate-700 bg-slate-900 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-slate-400">
                {s}
              </span>
            ))}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          Flash {(idx % flashes.length) + 1} / {flashes.length}
        </div>
      </div>

      <div className="grid flex-1 grid-cols-[1fr_1fr] gap-4">
        <div className="flex flex-col justify-center">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-6xl font-black text-white" data-testid="tv-play-suggestor-symbol">{cur.symbol}</span>
            <span className={`font-mono text-2xl font-black ${(cur.underlying_change_pct || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
              {(cur.underlying_change_pct || 0) >= 0 ? "+" : ""}{(cur.underlying_change_pct || 0).toFixed(2)}%
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-lg">
            <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono font-bold text-slate-200">
              {cur.play_desc}
            </span>
            <span className="text-sm text-slate-500">×{cur.contracts}</span>
            {cur.is_itm && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-black uppercase text-amber-300">ITM</span>}
            {cur.days_to_expiry !== null && cur.days_to_expiry !== undefined && (
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-black uppercase text-slate-300">{cur.days_to_expiry}d</span>
            )}
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Position P&amp;L</div>
            <div className={`font-mono text-5xl font-black ${pnlUp ? "text-emerald-300" : "text-red-300"}`} data-testid="tv-play-suggestor-pnl">
              {pnlUp ? "+" : ""}{(Number(cur.pnl_pct) || 0).toFixed(1)}%
            </div>
            <div className={`font-mono text-lg ${pnlUp ? "text-emerald-400/70" : "text-red-400/70"}`}>
              {pnlUp ? "+" : ""}${(Number(cur.pnl_dollars) || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
          <div>
            <div className="mb-1 text-[10px] font-black uppercase tracking-[0.32em] text-slate-400">Catalyst</div>
            <div className="text-base font-semibold leading-snug text-slate-100" data-testid="tv-play-suggestor-catalyst">
              {cur.catalyst || "Multiple signals aligning."}
            </div>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-amber-300">
              <Sparkles className="h-3 w-3" /> AI Verdict
            </div>
            <div className={`rounded-lg px-3 py-2 text-lg font-black leading-tight ${isGreen ? "bg-emerald-500/20 text-emerald-100" : "bg-red-500/20 text-red-100"}`} data-testid="tv-play-suggestor-verdict">
              {cur.verdict}
            </div>
            {cur.ai_line && (
              <div className="mt-2 text-sm leading-snug text-slate-300">
                {cur.ai_line}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


/* ============================================================================
   SLOT ROTATION SCHEDULE — four session-aware variants:
   - OPEN     : RTH — full lineup with plays / whales / hero-villain
   - PRE      : Pre-market takeover — 80% NEWS, 20% portfolio glance
   - AFTER    : After-hours takeover — 80% NEWS + earnings + upgrades
   - CLOSED   : Weekends / overnight — long-form (deep dives, IRA, ripe)
   ============================================================================ */
const SLOT_SCHEDULE_OPEN = [
  { type: "hero_villain",  durationMs: 10000 },
  { type: "sudden_alerts", durationMs: 12000 },
  { type: "play_suggestor",durationMs: 12000 },
  { type: "institutional", durationMs: 14000 },
  { type: "ripe_trades",   durationMs: 15000 },
  { type: "movers",        durationMs: 10000 },
  { type: "pulse",         durationMs: 9000, pulsePage: 1 },
  { type: "pulse",         durationMs: 9000, pulsePage: 2 },
  { type: "plays",         durationMs: 12000 },
  { type: "play_suggestor",durationMs: 12000 },
  { type: "world_markets", durationMs: 12000 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 0 },
  { type: "whale",         durationMs: 12000, pairIdx: 0 },
  { type: "ira",           durationMs: 12000 },
  { type: "institutional", durationMs: 14000 },
  { type: "news_upgrades", durationMs: 20000 },
  { type: "news_earnings", durationMs: 20000 },
  { type: "movers",        durationMs: 10000 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 1 },
  { type: "plays",         durationMs: 12000 },
  { type: "whale",         durationMs: 12000, pairIdx: 2 },
  { type: "business_news", durationMs: 20000 },
  { type: "political_news",durationMs: 20000 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 2 },
  { type: "short_squeeze", durationMs: 16000 },
];

// PRE-MARKET (4a-8:30a CT): 80% NEWS / 20% portfolio-glance
// Sequence: World Markets (Asia/EU overnight) → AI Brief → News Movers → Wall → Earnings → Pulse → Institutional → ...
const SLOT_SCHEDULE_PRE = [
  { type: "world_markets", durationMs: 14000 },  // ← what happened overnight
  { type: "news_ai_brief", durationMs: 20000 },
  { type: "news_movers",   durationMs: 20000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "news_earnings", durationMs: 20000 },
  { type: "pulse",         durationMs: 8000, pulsePage: 1 },   // 20% glance
  { type: "institutional", durationMs: 14000 },
  { type: "news_movers",   durationMs: 20000 },
  { type: "news_upgrades", durationMs: 20000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "world_markets", durationMs: 12000 },  // ← EU-open check
  { type: "news_earnings", durationMs: 20000 },
  { type: "short_squeeze", durationMs: 16000 },
  { type: "pulse",         durationMs: 8000, pulsePage: 2 },   // 20% glance
];

// AFTER-HOURS (3p-7p CT): 80% NEWS / 20% portfolio-glance  (earnings-heavy since post-close reports drop here)
const SLOT_SCHEDULE_AFTER = [
  { type: "news_ai_brief", durationMs: 20000 },
  { type: "play_suggestor",durationMs: 12000 },
  { type: "institutional", durationMs: 14000 },
  { type: "news_earnings", durationMs: 20000 },
  { type: "news_movers",   durationMs: 20000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "news_upgrades", durationMs: 20000 },
  { type: "pulse",         durationMs: 8000, pulsePage: 1 },   // 20% glance
  { type: "play_suggestor",durationMs: 12000 },
  { type: "world_markets", durationMs: 12000 },  // Asia opens 5pm CT
  { type: "news_earnings", durationMs: 20000 },
  { type: "news_movers",   durationMs: 20000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "business_news", durationMs: 20000 },
  { type: "political_news",durationMs: 20000 },
  { type: "institutional", durationMs: 14000 },
  { type: "short_squeeze", durationMs: 16000 },
  { type: "pulse",         durationMs: 8000, pulsePage: 2 },   // 20% glance
];

// CLOSED (overnight weekdays 8pm CT → 3am CT, plus holidays):
// Same philosophy as WEEKEND — no live-market data slots.
// Excluded: sudden_alerts, hero_villain, ripe_trades, movers  (all need live intraday data).
const SLOT_SCHEDULE_CLOSED = [
  { type: "news_ai_brief", durationMs: 20000 },
  { type: "play_suggestor",durationMs: 12000 },
  { type: "world_markets", durationMs: 14000 },  // Asia live overnight
  { type: "institutional", durationMs: 14000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "pulse",         durationMs: 9000, pulsePage: 1 },
  { type: "pulse",         durationMs: 9000, pulsePage: 2 },
  { type: "ira",           durationMs: 12000 },
  { type: "news_upgrades", durationMs: 20000 },
  { type: "world_markets", durationMs: 12000 },  // second look
  { type: "business_news", durationMs: 20000 },
  { type: "political_news",durationMs: 20000 },
  { type: "institutional", durationMs: 14000 },
  { type: "news_earnings", durationMs: 20000 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 0 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 1 },
  { type: "deep_dive",     durationMs: 12000, pickIdx: 2 },
  { type: "weekly_summary",durationMs: 16000 },
  { type: "short_squeeze", durationMs: 16000 },
];

const SLOT_SCHEDULE = SLOT_SCHEDULE_OPEN; // legacy alias for old references

// WEEKEND (Sat/Sun): NO live-data slots (no plays, hero_villain, sudden_alerts, movers, whale, ripe_trades)
// Focus: weekly recap + IRA + news + institutional + world markets (Asia trades Sunday night)
const SLOT_SCHEDULE_WEEKEND = [
  { type: "weekly_summary",durationMs: 16000 },  // ← the star of the weekend
  { type: "business_news", durationMs: 20000 },
  { type: "political_news",durationMs: 20000 },
  { type: "news_ai_brief", durationMs: 20000 },
  { type: "news_wall",     durationMs: 20000 },
  { type: "pulse",         durationMs: 10000, pulsePage: 1 },
  { type: "pulse",         durationMs: 10000, pulsePage: 2 },
  { type: "ira",           durationMs: 12000 },
  { type: "institutional", durationMs: 14000 },
  { type: "business_news", durationMs: 20000 },  // fetch again — always fresh
  { type: "political_news",durationMs: 20000 },
  { type: "news_upgrades", durationMs: 20000 },
  { type: "world_markets", durationMs: 14000 },
  { type: "news_earnings", durationMs: 20000 },
  { type: "short_squeeze", durationMs: 16000 },
  { type: "weekly_summary",durationMs: 16000 },
];

export default function TVDisplay() {
  const [cfg, setCfg] = useState(null);
  const [pulse, setPulse] = useState([]);
  const [whales, setWhales] = useState([]);
  const [marketQuotes, setMarketQuotes] = useState([]);
  const [cnbcQuotes, setCnbcQuotes] = useState([]);
  const [session, setSession] = useState("open"); // 'pre'|'open'|'after'|'closed'
  const [suddenAlerts, setSuddenAlerts] = useState({ active: [], archived: [] });
  const [bannerAlert, setBannerAlert] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [slotIdx, setSlotIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  // 1) Falcon View config polling (admin changes reflect within 15s)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const r = await api.get("/falcon-view/config"); if (!cancelled) setCfg(r.data); }
      catch (e) { /* fallback to defaults */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 2) Portfolio pulse polling (every 8s)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/portfolio-pulse?flash_threshold=5.0");
        if (cancelled) return;
        setPulse(r.data?.pulse || []);
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 2b) Market quotes polling (every 30s — for the fierce bottom scroll)
  // 🛡️ Same sticky-data protection as the CNBC row.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/market-quotes");
        if (cancelled) return;
        const incoming = r.data?.quotes || [];
        setMarketQuotes((prev) => {
          if (!prev || prev.length === 0) return incoming;
          if (incoming.length < Math.max(prev.length - 2, 15)) return prev;
          const prevKey = prev.map((q) => q.symbol).join(",");
          const nextKey = incoming.map((q) => q.symbol).join(",");
          if (prevKey === nextKey) {
            return prev.map((p) => {
              const match = incoming.find((q) => q.symbol === p.symbol);
              return match ? { ...p, price: match.price, change_pct: match.change_pct, change_abs: match.change_abs } : p;
            });
          }
          return incoming;
        });
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 2c) Sudden alerts polling — every 60s. When a new alert lands, flash banner.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/sudden-alerts");
        if (cancelled) return;
        const data = r.data || { active: [], archived: [] };
        setSuddenAlerts(data);
        const fresh = (data.active || []).find(
          (a) => !dismissedAlerts.has(`${a.symbol}:${a.created_at}`) && a.age_seconds < 90
        );
        if (fresh && (!bannerAlert || bannerAlert.symbol !== fresh.symbol)) {
          setBannerAlert(fresh);
        }
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 2d) CNBC-style top ticker polling (every 30s)
  // 🛡️ Sticky-forever data policy: once a symbol enters the ticker, we keep it
  // there and only mutate price/change values. Prevents Polygon partial
  // payloads from ever shrinking the doubled track (which caused the visible
  // "jumps back to AMZN after 4-5 stocks" loop bug).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/cnbc-ticker");
        if (cancelled) return;
        const incoming = (r.data?.quotes || []).filter((q) => q && q.symbol && q.price != null);
        setCnbcQuotes((prev) => {
          if (!prev.length) return incoming;
          // Patch prices of already-known symbols in place — no list mutation.
          const patched = prev.map((p) => {
            const m = incoming.find((q) => q.symbol === p.symbol);
            return m ? { ...p, price: m.price, change_pct: m.change_pct, change_abs: m.change_abs } : p;
          });
          // Append genuinely new symbols we've never seen (stable insertion).
          const known = new Set(prev.map((p) => p.symbol));
          const additions = incoming.filter((q) => !known.has(q.symbol));
          return additions.length ? [...patched, ...additions] : patched;
        });
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 2e) Market session polling (Chicago-time) — every 60s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/market-session");
        if (cancelled) return;
        setSession(r.data?.session || "closed");
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 3) Whale trades polling (every 30s)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/falcon-view/whales?min_premium=500000&limit=15");
        if (!cancelled) setWhales(r.data?.trades || []);
      } catch (e) { /* graceful */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // 4) Session-aware slot rotation. When session flips, jump to slot 0 of the new schedule.
  // Weekend gets its own schedule to avoid empty "loading" slots that need live market data.
  const isWeekend = (() => {
    const d = new Date().getDay(); // 0 = Sun, 6 = Sat
    return d === 0 || d === 6;
  })();
  const activeSchedule =
    isWeekend             ? SLOT_SCHEDULE_WEEKEND :
    session === "open"    ? SLOT_SCHEDULE_OPEN    :
    session === "pre"     ? SLOT_SCHEDULE_PRE     :
    session === "after"   ? SLOT_SCHEDULE_AFTER   :
                            SLOT_SCHEDULE_CLOSED;
  useEffect(() => { setSlotIdx(0); }, [session]);
  useEffect(() => {
    const currentSlot = activeSchedule[slotIdx % activeSchedule.length];
    const t = setTimeout(
      () => setSlotIdx((i) => (i + 1) % activeSchedule.length),
      currentSlot.durationMs
    );
    return () => clearTimeout(t);
  }, [slotIdx, session, activeSchedule]);

  // Skip-if-empty: any slot component may fire this event on mount when it has no data.
  // We advance immediately so viewers never stare at a "Nothing here" screen.
  useEffect(() => {
    const handler = () => setSlotIdx((i) => (i + 1) % activeSchedule.length);
    window.addEventListener("falcon-slot-empty", handler);
    return () => window.removeEventListener("falcon-slot-empty", handler);
  }, [activeSchedule.length]);

  const toggleFull = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen().catch(() => {}); setFullscreen(true); }
    else { document.exitFullscreen().catch(() => {}); setFullscreen(false); }
  };

  const sideRail = (cfg?.side_rail_items && cfg.side_rail_items.length ? cfg.side_rail_items : DEFAULT_SIDE_RAIL).filter((r) => r.enabled !== false);
  const brandTitle = cfg?.brand_title || "FALCON VIEW";
  const brandSubtitle = cfg?.brand_subtitle || "Powered by A V Tech";

  // Pick symbols for deep-dive slots — biggest movers first, from portfolio
  const deepDivePicks = pulse.filter((p) => p.symbol).slice(0, 6).map((p) => p.symbol);

  const current = activeSchedule[slotIdx % activeSchedule.length];
  let SlotComponent = null;
  if (current.type === "pulse") {
    SlotComponent = <PortfolioPulseSlot pulse={pulse} page={current.pulsePage || 1} />;
  } else if (current.type === "deep_dive") {
    const sym = deepDivePicks[current.pickIdx % Math.max(1, deepDivePicks.length)] || "AAPL";
    SlotComponent = <DeepDiveSlot symbol={sym} />;
  } else if (current.type === "whale") {
    const total = whales.length;
    if (total === 0) {
      SlotComponent = <WhaleSlot whales={[]} />;
    } else {
      const start = current.pairIdx % total;
      const pair = total === 1
        ? [whales[0]]
        : [whales[start], whales[(start + 1) % total]];
      SlotComponent = <WhaleSlot whales={pair} />;
    }
  } else if (current.type === "movers") {
    SlotComponent = <MoversSlot pulse={pulse} />;
  } else if (current.type === "hero_villain") {
    SlotComponent = <HeroVillainSlot />;
  } else if (current.type === "plays") {
    SlotComponent = <PlaysSlot />;
  } else if (current.type === "sudden_alerts") {
    SlotComponent = <SuddenAlertsSlot alerts={suddenAlerts} />;
  } else if (current.type === "ira") {
    SlotComponent = <IRASlot />;
  } else if (current.type === "trendspider") {
    SlotComponent = <TrendSpiderSlot />;
  } else if (current.type === "ripe_trades") {
    SlotComponent = <RipeTradesSlot />;
  } else if (current.type === "news_movers") {
    SlotComponent = <NewsMoversSlot session={session} />;
  } else if (current.type === "news_wall") {
    SlotComponent = <NewsWallSlot />;
  } else if (current.type === "news_earnings") {
    SlotComponent = <EarningsCatalystSlot />;
  } else if (current.type === "news_upgrades") {
    SlotComponent = <UpgradesDowngradesSlot />;
  } else if (current.type === "news_ai_brief") {
    SlotComponent = <AIBriefSlot session={session} />;
  } else if (current.type === "play_suggestor") {
    SlotComponent = <PlaySuggestorSlot />;
  } else if (current.type === "world_markets") {
    SlotComponent = <WorldMarketsSlot />;
  } else if (current.type === "institutional") {
    SlotComponent = <InstitutionalFlowSlot />;
  } else if (current.type === "weekly_summary") {
    SlotComponent = <WeeklySummarySlot />;
  } else if (current.type === "short_squeeze") {
    SlotComponent = <ShortSqueezeSlot />;
  } else if (current.type === "business_news") {
    SlotComponent = <BusinessNewsSlot />;
  } else if (current.type === "political_news") {
    SlotComponent = <PoliticalNewsSlot />;
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-[#040711] text-white" data-testid="tv-display-root">
      {/* Breaking-news overlay — runs 24/7 and pauses whatever slot is on */}
      <BreakingNewsOverlay />
      {/* HEADER — fierce branding */}
      <header className="flex items-center justify-between border-b-2 border-amber-500/30 bg-gradient-to-r from-black via-[#0a0300] to-black px-6 py-3 backdrop-blur">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-white" data-testid="tv-exit-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Exit
          </Link>
          <div className="flex items-center gap-3" data-testid="tv-brand">
            {/* Brand block intentionally minimal — user requested no Falcon View logo/badge */}
          </div>
          {/* Slot indicator dots — reflect the ACTIVE (session-aware) schedule */}
          <div className="ml-4 flex items-center gap-1">
            {activeSchedule.map((s, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === (slotIdx % activeSchedule.length) ? "w-8 bg-amber-400" : "w-1.5 bg-slate-700"}`}
                data-testid={`tv-slot-dot-${i}`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-6">
          <LiveClock session={session} />
          <button
            onClick={toggleFull}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white"
            data-testid="tv-fullscreen-btn"
          >
            {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            {fullscreen ? "Exit" : "Fullscreen"}
          </button>
        </div>
      </header>

      {/* MAIN — full-width rotating slot */}
      <main className="min-h-0 min-w-0 flex-1 overflow-hidden p-3" data-testid={`tv-slot-${current.type}`}>
        {SlotComponent}
      </main>

      {/* SUDDEN ALERT BANNER — floats over content when new spike detected */}
      {bannerAlert && (
        <SuddenAlertBanner
          alert={bannerAlert}
          onClose={() => {
            setDismissedAlerts((s) => new Set([...s, `${bannerAlert.symbol}:${bannerAlert.created_at}`]));
            setBannerAlert(null);
          }}
        />
      )}

      {/* BOTTOM RAIL — 3 stacked ticker rows:
          Row 1 (TOP)    · CNBC-style thin scroll of 40 mega-caps · Δ$ + Δ%
          Row 2 (MIDDLE) · Portfolio holdings (calm native TradingView widget)
          Row 3 (BOTTOM) · Fierce big-font market indices + commodities + crypto */}
      <footer className="border-t border-slate-800 bg-gradient-to-r from-black via-slate-950 to-black" data-testid="tv-bottom-rail">
        {/* Row 1 — CNBC-style top wire */}
        <div className="border-b border-slate-800/60" data-testid="tv-cnbc-tape">
          <CNBCTicker items={cnbcQuotes} />
        </div>
        {/* Row 2 — Portfolio (calm CNBC-style native TradingView widget) */}
        <div className="border-b border-slate-800/60" data-testid="tv-ticker-tape">
          <TVTickerTape items={pulse.map((p) => ({ symbol: p.symbol, name: p.symbol }))} />
        </div>
        {/* Row 3 — Market indices / commodities / crypto (FIERCE big scroll) */}
        <div data-testid="tv-markets-tape">
          <BigScrollTicker items={marketQuotes} testidPrefix="tv-mkt" />
        </div>
      </footer>
    </div>
  );
}
